import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import FloorPlanCanvas from './FloorPlanCanvas';
import ChartDesigner, { orderedEntries } from './ChartDesigner';
import { evaluateMove, suggestTablesFor, suggestConnectedTables, buildSeatMap, isChild } from './seatingMoves';
import { QRCodeSVG } from 'qrcode.react';
import { adminAuthHeader } from '../../../data/api';
import MiloThinking from '../_shared/MiloThinking';
import './auto-seating.css';

// Lazy: three.js + r3f + drei (~280 kB) split into their own chunk, fetched only
// when the host opens the 3D view — keeps this flag-OFF module out of the main bundle.
const Floor3D = React.lazy(() => import('./Floor3D'));

/**
 * Auto Seating — day-of seating planner.
 * Phase 1: guest pool (from RSVP) + the setup/table manager with full manual CRUD
 * (bulk-create, per-table edit/delete, shapes, statuses, personalities) + live
 * capacity readout. Floor-plan canvas (Phase 2), solver (Phase 3), governance +
 * version history (Phase 4), Milo (Phase 6), export (Phase 8) land next.
 */
const API = process.env.REACT_APP_BACKEND_URL;
const SHAPES = ['round', 'rectangle', 'square', 'oval', 'sweetheart', 'head'];
const SHAPE_ICON = { round: '◯', rectangle: '▭', square: '◻', oval: '⬭', sweetheart: '♡', head: '★' };
const SHAPE_LABEL = { round: 'Round', rectangle: 'Rectangle', square: 'Square', oval: 'Oval', sweetheart: 'Sweetheart', head: 'Head' };
const STATUSES = ['standard', 'vip', 'family', 'honor'];
const STATUS_LABEL = { standard: 'Standard', vip: 'VIP', family: 'Family', honor: 'Honor' };
const PERSONALITY_OPTS = [['', 'Anyone'], ['family', 'Family'], ['friends', 'Friends'], ['church', 'Church'], ['work', 'Work'], ['kids', 'Kids'], ['vip', 'VIP'], ['mixed', 'Mixed']];
const BLANK_TABLE = () => ({ label: '', shape: 'round', seat_count: 8, status: 'standard', personality: '', accessible: false, x: 0, y: 0, rotation: 0, w: 0, h: 0, notes: '' });
const PX_PER_FOOT = 12;   // floor-plan scale: 12 px ≈ 1 foot (a 5-ft round table ≈ 60px)
const pxToFt = (px) => (Number(px) > 0 ? Math.round((Number(px) / PX_PER_FOOT) * 10) / 10 : '');
const ftToPx = (ft) => Math.round((Number(ft) || 0) * PX_PER_FOOT);
const derivedDiaFt = (seats) => Math.round((Math.max(32, 13 + (Number(seats) || 8) * 2.1) * 2) / PX_PER_FOOT);
// Room features & landmarks — sized footprints so the floor plan reads as a real space and gives
// the venue a point of reference when shared. {t:type, ic:icon, l:label, w/h:px footprint, shape}.
const FIXTURE_TYPES = [
  // Landmarks — large reference features that shape the room (and where tables can go)
  { t: 'dance_floor', ic: '🪩', l: 'Dance floor', w: 170, h: 170, shape: 'rect' },
  { t: 'stage', ic: '🎤', l: 'Stage', w: 200, h: 80, shape: 'rect' },
  { t: 'fountain', ic: '⛲', l: 'Fountain', w: 110, h: 110, shape: 'circle' },
  { t: 'pool', ic: '🏊', l: 'Pool', w: 230, h: 120, shape: 'rect' },
  { t: 'tent', ic: '⛺', l: 'Tent / canopy', w: 200, h: 170, shape: 'rect' },
  { t: 'buffet', ic: '🍽', l: 'Buffet', w: 190, h: 56, shape: 'rect' },
  { t: 'bar', ic: '🍸', l: 'Bar', w: 150, h: 56, shape: 'rect' },
  { t: 'gift_table', ic: '🎁', l: 'Gift table', w: 92, h: 48, shape: 'rect' },
  { t: 'photo_booth', ic: '📸', l: 'Photo booth', w: 84, h: 84, shape: 'rect' },
  { t: 'entrance', ic: '🚶', l: 'Entrance', w: 120, h: 40, shape: 'rect' },
  { t: 'garden', ic: '🌿', l: 'Garden / tree', w: 96, h: 96, shape: 'circle' },
  // Smaller markers
  { t: 'cake', ic: '🎂', l: 'Cake', w: 50, h: 50, shape: 'circle' },
  { t: 'dj', ic: '🎧', l: 'DJ', w: 64, h: 56, shape: 'rect' },
  { t: 'screen', ic: '🖥', l: 'Screen', w: 72, h: 40, shape: 'rect' },
  { t: 'column', ic: '⬛', l: 'Column', w: 46, h: 46, shape: 'circle' },
  { t: 'window', ic: '▢', l: 'Window', w: 64, h: 28, shape: 'rect' },
  { t: 'floral', ic: '✿', l: 'Floral', w: 46, h: 46, shape: 'circle' },
  { t: 'exit', ic: '🚪', l: 'Exit', w: 46, h: 46, shape: 'rect' },
  { t: 'restroom', ic: '🚻', l: 'Restroom', w: 46, h: 46, shape: 'rect' },
];
const FIXTURE_DEF = Object.fromEntries(FIXTURE_TYPES.map((f) => [f.t, f]));
const FIXTURE_LABELS = Object.fromEntries(FIXTURE_TYPES.map((f) => [f.t, f.l]));

// Dismissible Milo coaching tips — guidance for first-timers, not a permanent banner.
// "Next" cycles; "Hide tips" stops them for good (per browser, via localStorage).
const SEATING_TIPS = [
  'Drag any table to rearrange the room — works in 2D and 3D.',
  'Tap a table to edit its seats, shape or VIP status — the editor also has a Delete.',
  'Add landmarks (dance floor, stage, bar, cake…) from the palette — then tap any one to resize, rotate or remove it.',
  'Upload a venue photo in 3D to lay your real room beneath the plan.',
  'Save a layout version anytime — restore or compare it later from the Optimize tab.',
  'Tell Milo how you’d like the room arranged, in your own words — he proposes, you decide.',
];
function useEscClose(onClose) {
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
}
function MiloTips() {
  const [off, setOff] = useState(() => { try { return localStorage.getItem('as_tips_off') === '1'; } catch (e) { return false; } });
  const [i, setI] = useState(0);
  if (off) return null;
  const hide = () => { try { localStorage.setItem('as_tips_off', '1'); } catch (e) { /* */ } setOff(true); };
  return (
    <div className="as-tip" role="note">
      <span className="as-orb2">M</span>
      <span className="as-tiptext"><b>Tip {((i % SEATING_TIPS.length) + 1)}/{SEATING_TIPS.length}</b> · {SEATING_TIPS[i % SEATING_TIPS.length]}</span>
      <button className="as-tipbtn" onClick={() => setI((n) => n + 1)}>Next ›</button>
      <button className="as-tipbtn ghost" onClick={hide}>Hide tips</button>
    </div>
  );
}

// Gold arc-ring gauge for the KPI strip — thin SVG donut, value/label centered. tone: gold|red|green.
function KpiRing({ pct = 0, tone = 'gold', size = 52, stroke = 4, children }) {
  const r = (size - stroke) / 2; const c = 2 * Math.PI * r;
  const off = c * (1 - Math.max(0, Math.min(1, pct)));
  const col = tone === 'red' ? '#c2705f' : tone === 'green' ? '#789A82' : 'url(#asgold)';
  return (
    <div className="as-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} aria-hidden="true">
        <defs><linearGradient id="asgold" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#9C7B31" /><stop offset="1" stopColor="#E4CE92" /></linearGradient></defs>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(120,110,86,.15)" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={col} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={off} transform={`rotate(-90 ${size / 2} ${size / 2})`} style={{ transition: 'stroke-dashoffset .6s cubic-bezier(.22,1,.36,1)' }} />
      </svg>
      <div className="as-ringc">{children}</div>
    </div>
  );
}

// Seats mode — a clean numbered-seat grid per table. Reads the solver's seat_index so families
// keep their consecutive seats; drag a guest to another seat (move), onto an occupied seat (swap),
// or onto another table's seat (move tables). All edits go through PATCH /seating/assignments —
// NO solver re-run. Not geometry optimization; exact round-table adjacency stays deferred.
function SeatGridView({ tables, assignments, poolList, groupColor, relGroups, onAssignSeat, onSwapSeats, onUnseat, selectedIds = [], onToggleSelect, onClearSelection, onRequestMove, onlyTableId = null }) {
  const byId = useMemo(() => { const m = {}; (poolList || []).forEach((g) => { m[g.id] = g; }); return m; }, [poolList]);
  const [drag, setDrag] = useState(null);   // {gid, table_id, seat_index}
  const [over, setOver] = useState(null);   // "tableId:seatIndex" currently hovered
  const [explainTid, setExplainTid] = useState(null);   // table whose Milo explanation is open
  const selSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  // Trace-based, DETERMINISTIC table explanation — grounded only in real seating data
  // (party_id grouping, relationship clusters, must-separate conflicts). No LLM, no fabrication.
  const explainTable = (tid) => {
    const occ = (assignments || []).filter((a) => a.table_id === tid).map((a) => byId[a.guest_id]).filter(Boolean);
    const byParty = {};
    occ.forEach((g) => { if (g.party_id) (byParty[g.party_id] = byParty[g.party_id] || []).push(g); });
    const families = Object.values(byParty).filter((m) => m.length >= 2)
      .map((m) => `${((m[0].name || '').trim().split(' ').slice(-1)[0]) || 'family'} family`);
    const clusters = new Set();
    occ.forEach((g) => (relGroups || []).forEach((grp) => { if ((grp.guest_ids || []).includes(g.id) && grp.name) clusters.add(grp.name); }));
    const ids = new Set(occ.map((g) => g.id));
    let conf = 0; occ.forEach((g) => (g.conflicts_with || []).forEach((c) => { if (ids.has(c)) conf += 1; }));
    conf = Math.round(conf / 2);
    const parts = [];
    if (families.length) parts.push(`Seats the ${families.join(' and ')}.`);
    else if (occ.length) parts.push(`Seats ${occ.length} guest${occ.length !== 1 ? 's' : ''}.`);
    else return 'No guests are seated here yet.';
    if (clusters.size) parts.push(`Shared circle: ${[...clusters].join(', ')}.`);
    parts.push(conf > 0 ? `${conf} seating conflict here — two guests marked must-separate are together.` : 'No seating conflicts.');
    parts.push(`Seating confidence: ${conf > 0 ? 'Needs attention' : 'High'}.`);
    return parts.join(' ');
  };

  const seatMap = useMemo(() => buildSeatMap(tables, assignments), [tables, assignments]);
  const shownTables = onlyTableId ? (tables || []).filter((t) => t.id === onlyTableId) : (tables || []);

  const drop = (tid, seatIndex) => {
    if (!drag) return;
    // Multi-select: dropping any selected guest moves the WHOLE selection to this table (②),
    // routed through the parent resolver so conflicts/accessibility/family splits get a heads-up.
    if (selectedIds.length > 1 && selSet.has(drag.gid) && onRequestMove) {
      onRequestMove(selectedIds, tid); setDrag(null); setOver(null); return;
    }
    const target = ((seatMap[tid] || [])[seatIndex - 1]) || null;
    if (target && target !== drag.gid) onSwapSeats(drag.gid, drag.table_id, drag.seat_index, target, tid, seatIndex);
    else if (target !== drag.gid) onAssignSeat(drag.gid, tid, seatIndex);
    setDrag(null); setOver(null);
  };

  if (!shownTables.length) return <div className="as-poolempty" style={{ padding: '18px 2px' }}>Add tables to see seats.</div>;
  return (
    <div className="as-seatgrid">
      {selectedIds.length > 1 && (
        <div className="as-selbar grid" role="status">
          <b>{selectedIds.length} selected</b>
          <span className="hint">Drag any one onto another table to move them together.</span>
          <button className="x" onClick={() => onClearSelection && onClearSelection()}>Clear</button>
        </div>
      )}
      {shownTables.map((t) => {
        const arr = seatMap[t.id] || [];
        const filled = arr.filter(Boolean).length;
        return (
          <div className="as-sgtable" key={t.id}>
            <div className="as-sghead">
              <span className="as-sgname">{t.label}</span>
              <span className="as-sghright">
                <span className="as-sgcount">{filled}/{t.seat_count} seated</span>
                <button className="as-sgwhy" title="Ask Milo why these guests are seated here"
                  onClick={() => setExplainTid((p) => (p === t.id ? null : t.id))}>✦ Why?</button>
              </span>
            </div>
            {explainTid === t.id && (
              <div className="as-sgexplain"><span className="m">✦ Milo</span> <span>{explainTable(t.id)}</span></div>
            )}
            <div className="as-sgseats">
              {arr.map((gid, i) => {
                const seatIndex = i + 1;
                const g = gid ? byId[gid] : null;
                const okey = `${t.id}:${seatIndex}`;
                return (
                  <div key={i} className={`as-sgseat ${g ? 'on' : ''} ${over === okey ? 'over' : ''}`}
                    onDragOver={(e) => { if (drag) { e.preventDefault(); setOver(okey); } }}
                    onDragLeave={() => setOver((o) => (o === okey ? null : o))}
                    onDrop={(e) => { e.preventDefault(); drop(t.id, seatIndex); }}>
                    <span className="as-sgnum">Seat {seatIndex}</span>
                    {g ? (
                      <span className={`as-sgguest ${selSet.has(gid) ? 'msel' : ''}`} draggable
                        onDragStart={() => setDrag({ gid, table_id: t.id, seat_index: seatIndex })}
                        onDragEnd={() => { setDrag(null); setOver(null); }}>
                        <button className={`as-gcheck ${selSet.has(gid) ? 'on' : ''}`} title={selSet.has(gid) ? 'Selected' : 'Select to move with others'}
                          onClick={(e) => { e.stopPropagation(); onToggleSelect && onToggleSelect(gid); }}>{selSet.has(gid) ? '✓' : ''}</button>
                        <span className="as-sgdot" style={{ background: (groupColor && groupColor[gid]) || 'var(--as-gold)' }} />
                        <span className="as-sggname">{g ? g.name : gid}</span>
                        <button className="as-sgx" title="Unseat" onClick={() => onUnseat(gid)}>✕</button>
                      </span>
                    ) : <span className="as-sgempty">Empty</span>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Tables-section bulk seating (host 2026-07-13) — pick several unseated guests (families come as
// one checkbox group) and seat them onto this table in a single action. The backend deals chairs
// family-adjacent and rejects the whole batch if the table lacks room (no half-seated families).
function BulkSeatPicker({ table, poolList, assignments, onConfirm, onClose }) {
  const [picked, setPicked] = useState(new Set());
  const seatedIds = useMemo(() => new Set((assignments || []).filter((a) => a.table_id).map((a) => a.guest_id)), [assignments]);
  const unseated = useMemo(() => (poolList || []).filter((g) => !seatedIds.has(g.id)), [poolList, seatedIds]);
  const groups = useMemo(() => {
    const map = new Map();
    unseated.forEach((g) => {
      const key = g.party_id ? `pty:${g.party_id}` : (g.household ? `hh:${String(g.household).toLowerCase()}` : `solo:${g.id}`);
      const name = g.party_id ? (g.household || 'Family') : (g.household || g.name);
      if (!map.has(key)) map.set(key, { key, name, members: [] });
      map.get(key).members.push(g);
    });
    const real = []; const singles = [];
    map.forEach((grp) => { if (grp.members.length >= 2) real.push(grp); else singles.push(grp.members[0]); });
    real.sort((a, b) => b.members.length - a.members.length || (a.name || '').localeCompare(b.name || ''));
    singles.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return { real, singles };
  }, [unseated]);
  const free = Math.max(0, (Number(table.seat_count) || 8) - (assignments || []).filter((a) => a.table_id === table.id).length);
  const toggle = (ids, on) => setPicked((p) => { const n = new Set(p); ids.forEach((id) => (on ? n.add(id) : n.delete(id))); return n; });
  const over = picked.size > free;
  useEffect(() => { const h = (e) => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h); }, [onClose]);
  return (
    <div className="as-ov" onClick={onClose}>
      <div className="as-modal as-bsp" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={`Seat guests at ${table.label || 'this table'}`}>
        <div className="as-bsphead">
          <div className="t">Seat guests at <b>{table.label || 'this table'}</b></div>
          <div className="d">{free} free seat{free === 1 ? '' : 's'} · families are seated on neighboring chairs automatically</div>
        </div>
        <div className="as-bspbody">
          {!unseated.length && <div className="as-poolempty">Everyone attending is already seated.</div>}
          {groups.real.map((grp) => {
            const ids = grp.members.map((g) => g.id);
            const allOn = ids.every((id) => picked.has(id));
            return (
              <div key={grp.key} className="as-bspgroup">
                <label className="as-bsprow head">
                  <input type="checkbox" checked={allOn} onChange={(e) => toggle(ids, e.target.checked)} />
                  <b>{grp.name}</b><span className="n">{grp.members.length} people</span>
                </label>
                {grp.members.map((g) => (
                  <label key={g.id} className="as-bsprow sub">
                    <input type="checkbox" checked={picked.has(g.id)} onChange={(e) => toggle([g.id], e.target.checked)} />
                    {g.name}
                  </label>
                ))}
              </div>
            );
          })}
          {groups.singles.length > 0 && (
            <div className="as-bspgroup">
              {groups.real.length > 0 && <div className="as-bsplab">Individual guests</div>}
              {groups.singles.map((g) => (
                <label key={g.id} className="as-bsprow">
                  <input type="checkbox" checked={picked.has(g.id)} onChange={(e) => toggle([g.id], e.target.checked)} />
                  {g.name}
                </label>
              ))}
            </div>
          )}
        </div>
        <div className="as-bspfoot">
          <span className={`as-bspcount ${over ? 'over' : ''}`}>{picked.size} selected{over ? ` — only ${free} chair${free === 1 ? '' : 's'} free` : ''}</span>
          <button className="as-btn" onClick={onClose}>Cancel</button>
          <button className="as-btn gold" disabled={!picked.size || over}
            onClick={() => onConfirm(Array.from(picked))}>Seat {picked.size || ''} guest{picked.size === 1 ? '' : 's'}</button>
        </div>
      </div>
    </div>
  );
}

// Milo seating concierge — a still-by-default premium presence in the lower-left of the seating
// screen. Surfaces DETERMINISTIC, grounded suggestions computed from the real plan + pool (families
// intact, isolated guest, table balance, unseated) as a conversational speech bubble — never an
// error alert, never fabricated reasoning. Animates only on hover/click or when a new suggestion
// appears (and respects prefers-reduced-motion via CSS).
// Host 2026-07-13: when Milo ASKS a question ("suggest a closer table?"), the card now carries the
// ANSWER button — "Suggest a table" lists where that guest actually knows people (real household/
// group/circle data), and Move routes through the same governed safeguards as every other move.
function MiloConcierge({ tables, assignments, poolList, relGroups, onRequestMove, ghostSeats, onReleaseGhosts }) {
  const suggestions = useMemo(() => {
    const byId = {}; (poolList || []).forEach((g) => { byId[g.id] = g; });
    const seated = (assignments || []).filter((a) => a.table_id);
    const seatedIds = new Set(seated.map((a) => a.guest_id));
    const tableOf = {}; seated.forEach((a) => { tableOf[a.guest_id] = a.table_id; });
    const occByT = {}; seated.forEach((a) => { (occByT[a.table_id] = occByT[a.table_id] || []).push(a.guest_id); });
    const out = [];
    // ghost seats FIRST (host 2026-07-13: live plan read "95 seated" vs 91 attending) — chairs still
    // held by guests who declined / were removed. Count is derived from the live plan itself; names
    // come from the server's ghost report. Releasing is host-confirmed and snapshotted, never automatic.
    const ghostCount = seated.filter((a) => !byId[a.guest_id]).length;
    if (ghostCount > 0) {
      const names = (ghostSeats || []).map((g) => `${g.name} (${g.table_label}, ${g.why})`).join(' · ');
      out.push({
        text: `${ghostCount} saved seat${ghostCount === 1 ? '' : 's'} belong${ghostCount === 1 ? 's' : ''} to guests no longer on the attending list${names ? `: ${names}` : ''}. They're quietly holding real chairs — releasing them frees the seats and corrects your counts.`,
        ghosts: true,
      });
    }
    // families intact (affirmation)
    const byParty = {};
    (poolList || []).forEach((g) => { if (g.party_id && seatedIds.has(g.id)) (byParty[g.party_id] = byParty[g.party_id] || []).push(g); });
    const intact = Object.values(byParty).filter((m) => m.length >= 2 && new Set(m.map((g) => tableOf[g.id])).size === 1);
    if (intact.length) {
      const m = intact[0]; const last = (m[0].name || '').trim().split(' ').slice(-1)[0] || 'family';
      out.push({ text: `The ${last} family is seated together. Seating confidence is high.` });
    }
    // isolated guest — a solo guest with no connection to their tablemates → an ANSWERABLE ask
    for (const a of seated) {
      const g = byId[a.guest_id]; if (!g || g.party_id) continue;
      const mates = (occByT[a.table_id] || []).filter((x) => x !== a.guest_id).map((x) => byId[x]).filter(Boolean);
      if (!mates.length) continue;
      const connected = mates.some((mm) => (g.household && g.household === mm.household) || (g.group_name && g.group_name === mm.group_name)
        || (relGroups || []).some((grp) => (grp.guest_ids || []).includes(g.id) && (grp.guest_ids || []).includes(mm.id)));
      if (!connected) { out.push({ text: `${g.name} may be sitting among guests they don’t know — would you like me to suggest a closer table?`, isolatedGuestId: g.id, isolatedName: g.name }); break; }
    }
    // table balance
    const fills = Object.entries(occByT).map(([t, m]) => { const tb = (tables || []).find((x) => x.id === t) || {}; return { n: m.length, c: tb.seat_count || 0, label: tb.label }; });
    const full = fills.find((f) => f.c && f.n >= f.c); const light = fills.find((f) => f.c && f.n > 0 && f.n * 2 < f.c);
    if (full && light) out.push({ text: `${light.label} is less than half full while others are full — you could even out the room.` });
    // unseated
    const un = (poolList || []).filter((g) => !seatedIds.has(g.id));
    if (un.length) out.push({ text: `${un.length} guest${un.length !== 1 ? 's aren’t' : ' isn’t'} seated yet — I can generate a full plan, or you can drag them in.` });
    if (!out.length) out.push({ text: 'Your seating looks balanced — families together, no conflicts. I’m here whenever you’d like a hand.' });
    return out.slice(0, 4);
  }, [tables, assignments, poolList, relGroups, ghostSeats]);

  const [idx, setIdx] = useState(0);
  const [pulse, setPulse] = useState(false);
  const [imgOk, setImgOk] = useState(true);
  const [picks, setPicks] = useState(null);   // computed candidates for the current ask, or {none:true}
  useEffect(() => { setIdx(0); }, [suggestions.length]);
  useEffect(() => { setPulse(true); setPicks(null); const t = setTimeout(() => setPulse(false), 900); return () => clearTimeout(t); }, [idx, suggestions]);
  const advance = () => setIdx((i) => (i + 1) % suggestions.length);
  const cur = suggestions[idx] || {};

  // The answer to Milo's question: rank tables where this guest actually knows people
  // (household / group / relationship circle) — deterministic, real data only.
  const suggestFor = (gid) => {
    const found = suggestConnectedTables(gid, { tables, assignments, poolList, relGroups, max: 3 });
    setPicks(found.length ? { list: found } : { none: true });
  };

  return (
    <div className="as-milo" role="status" aria-live="polite">
      <button className={`as-miloav ${pulse ? 'pulse' : ''}`} title="Milo — your seating concierge"
        onClick={() => { advance(); setPulse(true); }}>
        {imgOk ? <img src="/milo-avatar.png" alt="Milo" onError={() => setImgOk(false)} /> : <span className="fallback">M</span>}
      </button>
      <div className="as-milobubble">
        <div className="as-milohead">Milo · seating concierge</div>
        <div className="as-milotext">{cur.text}</div>
        {cur.ghosts && onReleaseGhosts && (
          <button className="as-milonext act" onClick={onReleaseGhosts}>✦ Release their chairs (a restore point is saved first)</button>
        )}
        {cur.isolatedGuestId && !picks && (
          <button className="as-milonext act" onClick={() => suggestFor(cur.isolatedGuestId)}>✦ Suggest a table</button>
        )}
        {picks && picks.none && (
          <div className="as-milopicks">
            <div className="as-milopick-empty">No table currently seats people {cur.isolatedName} knows — you could move a friend to their table, or ask me for a full plan in ✦ Optimize.</div>
          </div>
        )}
        {picks && picks.list && (
          <div className="as-milopicks">
            {picks.list.map((p) => (
              <div className="as-milopick" key={p.tableId}>
                <div className="bd">
                  <b>{p.label}</b> — knows {p.connections} {p.connections === 1 ? 'person' : 'people'} there ({p.names.join(', ')}) · {p.open} seat{p.open === 1 ? '' : 's'} free
                </div>
                <button className="as-btn gold sm" onClick={() => { setPicks(null); if (onRequestMove) onRequestMove([cur.isolatedGuestId], p.tableId); }}>Move here</button>
              </div>
            ))}
          </div>
        )}
        {suggestions.length > 1 && (
          <button className="as-milonext" onClick={advance}>Next suggestion ›</button>
        )}
      </div>
    </div>
  );
}

// MoveResolver (③) — when a move would over-fill a table, seat must-separate guests together,
// place an accessibility guest at a non-accessible table, or split a family, this modal lets the
// host resolve it deterministically: re-target the whole group to a table that fits, relocate
// current occupants to make room, keep some movers back, and acknowledge soft warnings. It never
// guesses — every option is grounded in real capacity/conflict/accessibility data. Apply unlocks
// only when nothing is blocking and any family-split warning is acknowledged.
function MoveResolver({ req, tables, assignments, poolList, onCancel, onApply }) {
  const { movingIds, targetTableId } = req;
  const byId = useMemo(() => { const m = {}; (poolList || []).forEach((g) => { m[g.id] = g; }); return m; }, [poolList]);
  const tablesById = useMemo(() => { const m = {}; (tables || []).forEach((t) => { m[t.id] = t; }); return m; }, [tables]);
  const target = tablesById[targetTableId] || {};
  const [keepBack, setKeepBack] = useState(() => new Set());   // mover ids to NOT move
  const [bumps, setBumps] = useState({});                      // occupantId -> destTableId | '__unseat__'
  const [ackWarn, setAckWarn] = useState(false);
  useEscClose(onCancel);

  const base = useMemo(() => evaluateMove({ movingIds, targetTableId, tables, assignments, poolList }), [movingIds, targetTableId, tables, assignments, poolList]);
  const finalMovers = movingIds.filter((id) => !keepBack.has(id));

  // Synthetic assignments reflecting the chosen relocations, so we can re-evaluate the final state.
  const simAssignments = useMemo(() => (assignments || []).map((a) => {
    if (bumps[a.guest_id]) { const d = bumps[a.guest_id]; return { ...a, table_id: d === '__unseat__' ? null : d }; }
    return a;
  }), [assignments, bumps]);
  const finalEval = useMemo(() => evaluateMove({ movingIds: finalMovers, targetTableId, tables, assignments: simAssignments, poolList }),
    [finalMovers, targetTableId, tables, simAssignments, poolList]);

  // A quick "just move everyone to a table that fits" path — the cleanest resolution of all.
  const betterFits = useMemo(() => suggestTablesFor(movingIds, { tables, assignments, poolList, excludeTableId: targetTableId }).slice(0, 4),
    [movingIds, tables, assignments, poolList, targetTableId]);

  const occupants = base.bumpCandidates;     // current target occupants (excludes movers)
  const needAck = finalEval.warnings.length > 0;
  const noMovers = finalMovers.length === 0;
  const resolved = !finalEval.blocking && (!needAck || ackWarn) && !noMovers
    && Object.entries(bumps).every(([, d]) => !!d);

  const nm = (id) => (byId[id] || {}).name || id;
  const toggleKeep = (id) => setKeepBack((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const setBump = (id, dest) => setBumps((b) => { const n = { ...b }; if (!dest) delete n[id]; else n[id] = dest; return n; });

  const apply = () => {
    const bumpList = Object.entries(bumps).filter(([, d]) => d).map(([guestId, d]) => ({ guestId, toTableId: d === '__unseat__' ? null : d }));
    onApply({ moverIds: finalMovers, targetTableId, bumps: bumpList });
  };

  return (
    <div className="as-ov" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="as-modal as-mvr" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Resolve seating move">
        <div className="as-mhead">
          <div>
            <div className="as-mvr-eyebrow">Before this move</div>
            <div className="mt">Moving {movingIds.length} to {target.label || 'this table'}</div>
          </div>
          <button className="as-mx" onClick={onCancel} aria-label="Cancel">✕</button>
        </div>

        <div className="as-mbody">
          {/* the heads-up — what's problematic */}
          <div className="as-mvr-issues">
            {!base.capacity.ok && (
              <div className="as-mvr-issue cap"><b>Not enough room.</b> {target.label || 'This table'} has {base.capacity.open} open seat{base.capacity.open === 1 ? '' : 's'}, you're moving {base.capacity.needed}.</div>
            )}
            {base.conflicts.map((c, i) => (
              <div className="as-mvr-issue conf" key={`c${i}`}><b>Conflict.</b> {c.a.name} and {c.b.name} are marked must-separate — they can't share a table.</div>
            ))}
            {base.accessibility.map((a, i) => (
              <div className="as-mvr-issue acc" key={`a${i}`}><b>Accessibility.</b> {a.name} needs an accessible table — {target.label || 'this one'} isn't marked ♿.</div>
            ))}
            {base.familySplits.map((f, i) => (
              <div className="as-mvr-issue fam" key={`f${i}`}>The <b>{f.name}</b> family would be split ({f.atTarget} of {f.total} here, the rest at {f.tables.filter((x) => x !== (target.label)).join(', ') || 'another table'}).</div>
            ))}
          </div>

          {/* fastest fix: move the whole group somewhere that fits */}
          {betterFits.length > 0 && (
            <div className="as-mvr-sec">
              <div className="as-mvr-lab">Move the whole group to a table that fits</div>
              <div className="as-mvr-fits">
                {betterFits.map((t) => (
                  <button key={t.id} className="as-mvr-fit" onClick={() => onApply({ moverIds: movingIds, targetTableId: t.id, bumps: [] })}>
                    {t.label} <span>· {t.open} open{t.accessible ? ' · ♿' : ''}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* or: keep some movers back */}
          {movingIds.length > 1 && (
            <div className="as-mvr-sec">
              <div className="as-mvr-lab">…or leave some where they are</div>
              <div className="as-mvr-chips">
                {movingIds.map((id) => (
                  <button key={id} className={`as-mvr-chip ${keepBack.has(id) ? 'back' : 'go'}`} onClick={() => toggleKeep(id)}>
                    {nm(id)} <span>{keepBack.has(id) ? 'stays' : 'moves'}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* or: relocate current occupants to make room / clear a conflict */}
          {occupants.length > 0 && (
            <div className="as-mvr-sec">
              <div className="as-mvr-lab">…or move someone already at {target.label || 'this table'}</div>
              <div className="as-mvr-occ">
                {occupants.map((o) => {
                  const dests = suggestTablesFor([o.id], { tables, assignments, poolList, excludeTableId: targetTableId }).slice(0, 8);
                  return (
                    <div className="as-mvr-occrow" key={o.id}>
                      <span className="nm">{o.name}{o.protected ? ' 🔒' : ''}{o.party ? ' · family' : ''}</span>
                      <select className="as-sel sm" value={bumps[o.id] || ''} onChange={(e) => setBump(o.id, e.target.value)}>
                        <option value="">Keep here</option>
                        {dests.map((d) => <option key={d.id} value={d.id}>→ {d.label} ({d.open} open)</option>)}
                        <option value="__unseat__">→ Unseat</option>
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* live status */}
          <div className={`as-mvr-status ${resolved ? 'ok' : 'block'}`}>
            {noMovers ? 'No one is moving — nothing to apply.'
              : finalEval.blocking ? `Still blocked: ${finalEval.summary}.`
              : needAck ? 'Ready once you confirm the family split below.'
              : 'Looks clear — ready to apply.'}
          </div>

          {needAck && (
            <label className="as-mvr-ack">
              <input type="checkbox" checked={ackWarn} onChange={(e) => setAckWarn(e.target.checked)} />
              <span>I understand a family would be split across tables, and want to continue.</span>
            </label>
          )}
        </div>

        <div className="as-mfoot">
          <button className="as-btn" onClick={onCancel}>Cancel</button>
          <button className="as-btn gold" disabled={!resolved} onClick={apply}>Apply move</button>
        </div>
      </div>
    </div>
  );
}

export default function AutoSeatingPage() {
  const [plan, setPlan] = useState(null);
  const [pool, setPool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('plan');
  const [clearOpen, setClearOpen] = useState(false);
  const [editingFix, setEditingFix] = useState(null);
  const [editing, setEditing] = useState(null);       // {table, isNew}
  const [confirmDel, setConfirmDel] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [chartOpen, setChartOpen] = useState(false);   // the Seating Chart Designer overlay
  const [seatFocusTid, setSeatFocusTid] = useState(null);   // Tables section: table whose seat grid is open
  const [bulkSeatT, setBulkSeatT] = useState(null);         // Tables section: table getting bulk guests
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [conflictOpen, setConflictOpen] = useState(false);
  const [viewMode, setViewMode] = useState('2d');
  // Presentation aids (host 2026-07-21): lock = view-only floor plan (nothing can be
  // accidentally moved while presenting — orbit/pan/zoom stay live); full = the floor
  // plan alone fills the screen (portal to <body>, same reason as the Designer).
  const [floorLocked, setFloorLocked] = useState(false);
  const [floorFull, setFloorFull] = useState(false);
  useEffect(() => {
    if (!floorFull) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setFloorFull(false); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [floorFull]);
  const [fixOpen, setFixOpen] = useState(false);
  const [layoutMsg, setLayoutMsg] = useState('');
  const [errMsg, setErrMsg] = useState('');
  const [pmBusy, setPmBusy] = useState(false);   // plan-mutation in-flight (prevents double-submit)
  const pmRef = useRef(false);
  const [showGuests, setShowGuests] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);      // multi-select (②) — guests chosen to move as a unit
  const [moveReq, setMoveReq] = useState(null);            // {movingIds, targetTableId} — drives the MoveResolver (③)

  // Granular fetchers so we can refresh ONE thing without the full-page loading swap.
  const fetchPlan = useCallback(async () => { try { const r = await axios.get(`${API}/api/seating/plan`); setPlan(r.data); return r.data; } catch (e) { return null; } }, []);
  const fetchPool = useCallback(async () => { try { const g = await axios.get(`${API}/api/seating/pool`, { headers: adminAuthHeader() }); setPool(g.data); return g.data; } catch (e) { return null; } }, []);
  const fetchAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchPlan(), fetchPool()]);
    setLoading(false);
  }, [fetchPlan, fetchPool]);
  // Silent refresh after seating a guest: take the plan the PATCH already returned and refresh the
  // pool's seated/unseated state WITHOUT toggling `loading` — that swap remounts the canvas and
  // throws the scroll back to the top, which is brutal when seating 100 guests one by one.
  const refreshAfterAssign = useCallback(async (planFromPatch) => {
    const y = (typeof window !== 'undefined') ? window.scrollY : 0;
    if (planFromPatch) setPlan(planFromPatch); else await fetchPlan();
    await fetchPool();
    if (typeof window !== 'undefined' && Math.abs(window.scrollY - y) > 2) window.scrollTo({ top: y });
  }, [fetchPlan, fetchPool]);
  // Full refresh without the loading swap — used by the Optimize tab after apply/undo/restore.
  const refreshSilent = useCallback(async () => { await Promise.all([fetchPlan(), fetchPool()]); }, [fetchPlan, fetchPool]);
  useEffect(() => { fetchAll(); }, [fetchAll]);
  const flash = useCallback((m) => { setErrMsg(m); window.setTimeout(() => setErrMsg(''), 4500); }, []);
  useEffect(() => { if (!confirmDel) return undefined; const h = (e) => { if (e.key === 'Escape') setConfirmDel(null); }; window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h); }, [confirmDel]);
  useEffect(() => { if (!clearOpen) return undefined; const h = (e) => { if (e.key === 'Escape') setClearOpen(false); }; window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h); }, [clearOpen]);

  const tables = (plan && plan.tables) || [];
  const summary = (plan && plan.summary) || {};
  const ps = (pool && pool.summary) || {};
  const assignments = (plan && plan.assignments) || [];
  const poolList = (pool && pool.pool) || [];
  const relGroups = (plan && plan.relationship_groups) || [];
  const fixtures = (plan && plan.fixtures) || [];
  const floorImage = (plan && plan.floor_image) || null;
  const groupColor = useMemo(() => {
    const m = {};
    relGroups.forEach((grp) => { (grp.guest_ids || []).forEach((gid) => { if (!m[gid]) m[gid] = grp.color || '#C6A75A'; }); });
    return m;
  }, [relGroups]);
  const guestsById = useMemo(() => { const m = {}; poolList.forEach((g) => { m[g.id] = g; }); return m; }, [poolList]);
  const seatMapMain = useMemo(() => buildSeatMap(tables, assignments), [tables, assignments]);
  const conflictInfo = useMemo(() => {
    const byId = {}; poolList.forEach((g) => { byId[g.id] = g; });
    const byTable = {}; assignments.forEach((a) => { (byTable[a.table_id] = byTable[a.table_id] || []).push(a.guest_id); });
    const bad = [];
    Object.entries(byTable).forEach(([tid, ids]) => {
      const hit = ids.some((id) => ((byId[id] && byId[id].conflicts_with) || []).some((x) => ids.includes(x)));
      if (hit) { const t = tables.find((tt) => tt.id === tid) || {}; bad.push({ table: t.label || 'a table' }); }
    });
    return bad;
  }, [assignments, poolList, tables]);
  const conflictCount = conflictInfo.length;
  // Ghost seats from RSVP flips (host 2026-07-21 — the "Guest" on the roster):
  // an assignment whose guest left the attending pool is invisible on the canvas
  // and prints as an anonymous "Guest". Surface it loudly with a one-click release.
  // Guarded on poolList being loaded so nothing flashes during the initial fetch.
  const staleSeats = useMemo(() => {
    if (!poolList.length) return [];
    const tById = {}; tables.forEach((t) => { tById[t.id] = t; });
    return assignments
      .filter((a) => !guestsById[a.guest_id])
      .map((a) => ({ gid: a.guest_id, table: (tById[a.table_id] || {}).label || 'a table', seat: a.seat_index }));
  }, [assignments, guestsById, poolList, tables]);
  // Accessibility violations (warning mode) — needs-guest seated at a non-accessible table while
  // accessible tables exist; plus a setup nudge when there are needs but no accessible table is marked.
  const accessWarnings = useMemo(() => {
    const byId = {}; poolList.forEach((g) => { byId[g.id] = g; });
    const tById = {}; tables.forEach((t) => { tById[t.id] = t; });
    const hasAccessible = tables.some((t) => t.accessible);
    const violations = !hasAccessible ? [] : assignments
      .filter((a) => (byId[a.guest_id] || {}).accessibility_needs && !((tById[a.table_id] || {}).accessible))
      .map((a) => ({ name: (byId[a.guest_id] || {}).name || 'A guest', table: (tById[a.table_id] || {}).label || 'a table' }));
    const needsTotal = poolList.filter((g) => g.accessibility_needs).length;
    return { violations, needsTotal, noAccessibleButHasNeeds: !hasAccessible && needsTotal > 0 };
  }, [assignments, poolList, tables]);
  const totalConflicts = conflictCount + accessWarnings.violations.length;

  const saveTables = async (nextTables) => {
    setSaving(true);
    try { const r = await axios.put(`${API}/api/seating/plan`, { tables: nextTables }); setPlan(r.data.plan); }
    catch (e) { flash('Couldn’t save the table — try again.'); }
    setSaving(false);
  };
  const saveTable = (form) => {
    const next = [...tables];
    if (editing.isNew) next.push(form);
    else { const i = next.findIndex((t) => t.id === editing.table.id); if (i >= 0) next[i] = { ...next[i], ...form }; else next.push(form); }
    setEditing(null); saveTables(next);
  };
  const removeTable = async (t) => {
    setConfirmDel(null);
    if (!t.id) return;
    setSaving(true);
    try { const r = await axios.delete(`${API}/api/seating/tables/${t.id}`); setPlan(r.data.plan); }
    catch (e) { /* */ }
    setSaving(false);
  };
  const bulkAdd = async ({ count, seat_count, shape, name_prefix }) => {
    setBulkOpen(false); setSaving(true);
    try {
      const r = await axios.post(`${API}/api/seating/tables/bulk`, {
        count: Number(count) || 1, seat_count: Number(seat_count) || 8, shape, name_prefix: name_prefix || 'Table',
      });
      setPlan(r.data.plan);
    } catch (e) { /* */ }
    setSaving(false);
  };

  // Floor-plan handlers (Phase 2) — all go through the governed seating endpoints.
  const moveTable = async (id, x, y) => { try { const r = await axios.patch(`${API}/api/seating/tables/${id}`, { x, y }); setPlan(r.data.plan); } catch (e) { flash('Couldn’t move that table — try again.'); } };
  const assignGuest = async (guestId, tableId) => { try { const r = await axios.patch(`${API}/api/seating/assignments`, { guest_id: guestId, table_id: tableId }); await refreshAfterAssign(r.data.plan); } catch (e) { flash('Couldn’t seat that guest — try again.'); } };
  // Seat-level move (Seats mode). Goes through the SAME governed PATCH endpoint with a seat_index —
  // NO solver re-run. Supports move-to-seat and move-between-tables. Swaps are done by the caller
  // issuing two seat moves. Re-uses the scroll-preserving refresh.
  const assignSeat = async (guestId, tableId, seatIndex) => {
    try { const r = await axios.patch(`${API}/api/seating/assignments`, { guest_id: guestId, table_id: tableId, seat_index: seatIndex }); await refreshAfterAssign(r.data.plan); }
    catch (e) { flash((e.response && e.response.data && e.response.data.detail) || 'Couldn’t move that guest — try again.'); }
  };
  // Swap two seated guests — ONE atomic backend operation (POST /seating/seat-swap), one guest at a
  // time. Used when a guest is dropped on an occupied seat (2D and 3D). An interrupted swap can no
  // longer leave both guests on one chair (the old two-PATCH sequence could).
  const swapSeats = async (gidA, tA, sA, gidB, tB, sB) => {
    if (pmRef.current) return; pmRef.current = true; setPmBusy(true);
    try {
      const r = await axios.post(`${API}/api/seating/seat-swap`, { guest_a: gidA, guest_b: gidB });
      await refreshAfterAssign(r.data.plan);
    } catch (e) { flash((e.response && e.response.data && e.response.data.detail) || 'Couldn’t swap those seats — try again.'); }
    finally { pmRef.current = false; setPmBusy(false); }
  };
  // Group seating — seat a whole household/party/relationship-group together with ONE bulk call.
  // All-or-nothing on the backend (rejects if the table lacks enough free chairs, so no half-seated
  // families), and chairs are dealt family-adjacent server-side. One refresh = no scroll-jump.
  const seatGroup = async (guestIds, tableId) => {
    if (pmRef.current) return { seated: 0, requested: (guestIds || []).length };
    pmRef.current = true; setPmBusy(true);
    let seated = 0;
    try {
      try {
        const r = await axios.post(`${API}/api/seating/assignments/bulk`, { guest_ids: guestIds || [], table_id: tableId });
        seated = r.data.seated || 0;
        try { await refreshAfterAssign(r.data.plan); } catch (e) { /* refresh is best-effort; never strand the lock */ }
      } catch (e) {
        flash((e.response && e.response.data && e.response.data.detail) || 'That group couldn’t be seated — the table may be full. Try a larger table.');
      }
    } finally {
      // Always release the mutation lock — a failure must not permanently
      // freeze further plan edits until a page reload.
      pmRef.current = false; setPmBusy(false);
    }
    return { seated, requested: (guestIds || []).length };
  };
  const unseatGuest = async (guestId) => { try { const r = await axios.patch(`${API}/api/seating/assignments`, { guest_id: guestId, table_id: null }); await refreshAfterAssign(r.data.plan); } catch (e) { flash('Couldn’t unseat that guest — try again.'); } };
  // Release ghost seats — chairs still held by non-attending guests. Host-confirmed via Milo's
  // card; the backend snapshots a restore point first, so this is always reversible.
  const releaseGhosts = async () => {
    if (pmRef.current) return; pmRef.current = true; setPmBusy(true);
    try {
      const r = await axios.post(`${API}/api/seating/release-ghosts`, { confirm: true });
      await refreshAfterAssign(r.data.plan);
      const n = (r.data.released || []).length;
      flash(n ? `✓ Released ${n} ghost seat${n === 1 ? '' : 's'} — a restore point was saved to Version History first.` : 'No ghost seats to release.');
    } catch (e) { flash('Couldn’t release those seats — try again.'); }
    finally { pmRef.current = false; setPmBusy(false); }
  };

  // ───────── Move orchestration (①②③) ─────────
  const toggleSelect = (id) => setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.concat(id)));
  const clearSelection = () => setSelectedIds([]);

  // Run one or more table moves + any occupant relocations through the SAME governed PATCH endpoint,
  // sequentially (Guests stays the source of truth), then refresh once (no scroll-jump). bumps first
  // so seats are freed before the movers land.
  const applyMove = async ({ moverIds, targetTableId, bumps }) => {
    if (pmRef.current) return; pmRef.current = true; setPmBusy(true);
    let lastPlan = null;
    try {
      for (const b of (bumps || [])) {
        const r = await axios.patch(`${API}/api/seating/assignments`, { guest_id: b.guestId, table_id: b.toTableId });
        lastPlan = r.data.plan;
      }
      for (const gid of (moverIds || [])) {
        const r = await axios.patch(`${API}/api/seating/assignments`, { guest_id: gid, table_id: targetTableId });
        lastPlan = r.data.plan;
      }
      try { await refreshAfterAssign(lastPlan); } catch (e) { /* best-effort refresh */ }
    } catch (e) { flash('Couldn’t complete that move — please try again.'); }
    finally { pmRef.current = false; setPmBusy(false); setMoveReq(null); clearSelection(); }
  };

  // The single entry point every move flows through: drag on the floor, drag in the Seats grid,
  // multi-select group move, inline table change in the roster. Clean → apply immediately;
  // anything blocking or worth a heads-up → open the resolver.
  const requestMove = (movingIds, targetTableId) => {
    const ids = Array.from(new Set((movingIds || []).filter(Boolean)));
    if (!ids.length || !targetTableId) return;
    const ev = evaluateMove({ movingIds: ids, targetTableId, tables, assignments, poolList });
    if (ev.noChange) { clearSelection(); return; }
    if (ev.clean) { applyMove({ moverIds: ev.movers, targetTableId, bumps: [] }); return; }
    setMoveReq({ movingIds: ids, targetTableId });
  };

  // ───────── Roster export (④) — print + governed Communications hand-off ─────────
  const buildRoster = () => {
    const byIdL = {}; poolList.forEach((g) => { byIdL[g.id] = g; });
    const byTable = {}; assignments.forEach((a) => { if (a.table_id) (byTable[a.table_id] = byTable[a.table_id] || []).push(a.guest_id); });
    const rows = tables.map((t) => {
      const names = (byTable[t.id] || []).map((id) => (byIdL[id] || {}).name).filter(Boolean).sort();
      return { label: t.label || 'Table', cap: t.seat_count || 8, names };
    });
    const seatedCount = assignments.filter((a) => a.table_id).length;
    return { rows, seatedCount };
  };
  // The old fixed-format roster print was replaced by the Seating Chart Designer (host
  // 2026-07-13): every "print the roster" path now opens the live design surface instead.
  // The host's design choices persist with the event in plan.settings.chart_design.
  const saveChartDesign = async (design) => {
    const r = await axios.put(`${API}/api/seating/plan`, { settings: { ...((plan && plan.settings) || {}), chart_design: design } });
    setPlan(r.data.plan);
  };
  // Governed hand-off: stash a prefilled draft (subject + HTML roster body) and ask the
  // Communications module to open in compose mode. The host picks recipients and sends there —
  // nothing is ever sent automatically from Seating.
  const shareRosterViaComms = () => {
    const { rows, seatedCount } = buildRoster();
    if (!seatedCount) { flash('No one is seated yet — seat some guests first.'); return; }
    const bodyRows = rows.map((r) => `<h4 style="margin:14px 0 4px;font-family:Georgia,serif">${esc(r.label)} — ${r.names.length}/${r.cap}</h4><ol>${r.names.map((n) => `<li>${esc(n)}</li>`).join('') || '<li>—</li>'}</ol>`).join('');
    const subject = `Seating roster — ${(plan && plan.name) || 'Our Celebration'}`;
    const body = `<p>Here is the current seating roster (${seatedCount} seated across ${tables.length} tables).</p>${bodyRows}`;
    try {
      sessionStorage.setItem('communications_seating_prefill', JSON.stringify({ type: 'SEATING_ROSTER_EXPORT', purpose: 'custom', channel: 'email', subject, body }));
      window.dispatchEvent(new CustomEvent('open-communications-compose', { detail: { source: 'seating' } }));
      flash('Opening Communications with your roster — choose recipients and send there. Nothing sends automatically.');
    } catch (e) { flash('Couldn’t open Communications — try the Print option instead.'); }
  };
  const protectGuest = async (guestId, tableId, next) => { if (!tableId) return; try { const r = await axios.patch(`${API}/api/seating/assignments`, { guest_id: guestId, table_id: tableId, protected: next }); setPlan(r.data.plan); } catch (e) { flash('Couldn’t update that guest — try again.'); } };
  const saveGroups = async (groups) => { try { const r = await axios.put(`${API}/api/seating/plan`, { relationship_groups: groups }); setPlan(r.data.plan); } catch (e) { flash('Couldn’t save groups — try again.'); } };
  // X6 — mark/unmark two guests as "shouldn't sit together". Writes conflicts_with on BOTH guests
  // (backend keeps the Guests module as the source of truth); refresh the pool silently afterward.
  const markConflict = async (guestA, guestB, action = 'add') => {
    try { await axios.post(`${API}/api/seating/conflicts`, { guest_a: guestA, guest_b: guestB, action }); await fetchPool(); }
    catch (e) { flash(action === 'remove' ? 'Couldn’t remove that conflict — try again.' : 'Couldn’t mark that conflict — try again.'); }
  };
  // plan-level PUTs are guarded by pmRef so rapid clicks can't double-submit / clobber each other.
  const saveFixtures = async (next) => { if (pmRef.current) return; pmRef.current = true; setPmBusy(true); try { const r = await axios.put(`${API}/api/seating/plan`, { fixtures: next }); setPlan(r.data.plan); } catch (e) { flash('Couldn’t update fixtures — try again.'); } pmRef.current = false; setPmBusy(false); };
  const addFixture = (type) => { const d = FIXTURE_DEF[type] || {}; saveFixtures([...fixtures, { type, label: d.l || type, x: 450, y: 300, rotation: 0, w: d.w || 60, h: d.h || 60, shape: d.shape || 'rect' }]); };
  const moveFixture = (id, x, y) => saveFixtures(fixtures.map((f) => (f.id === id ? { ...f, x, y } : f)));
  const removeFixture = (id) => saveFixtures(fixtures.filter((f) => f.id !== id));
  const saveFixtureEdit = (updated) => { setEditingFix(null); saveFixtures(fixtures.map((x) => (x.id === updated.id ? updated : x))); };
  // Venue photo → downscaled JPEG data-URL stored in settings.floor_image (merge, don't clobber other settings).
  const saveSettings = async (patch) => { if (pmRef.current) return; pmRef.current = true; setPmBusy(true); try { const r = await axios.put(`${API}/api/seating/plan`, { settings: { ...((plan && plan.settings) || {}), ...patch } }); setPlan(r.data.plan); } catch (e) { flash('Couldn’t save — try again.'); } pmRef.current = false; setPmBusy(false); };
  const pickPhoto = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 1600; let { width, height } = img;
        if (width > max || height > max) { const r = Math.min(max / width, max / height); width = Math.round(width * r); height = Math.round(height * r); }
        const c = document.createElement('canvas'); c.width = width; c.height = height;
        c.getContext('2d').drawImage(img, 0, 0, width, height);
        saveSettings({ floor_image: c.toDataURL('image/jpeg', 0.82) });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };
  const removePhoto = () => { if (window.confirm('Remove the venue photo from the floor?')) saveSettings({ floor_image: null }); };
  // Snapshot the whole arrangement (tables + positions + fixtures + groups + photo) as a named,
  // restorable version. The host stays the final decision-maker — restore is in the Optimize tab.
  const saveLayout = async () => {
    if (pmRef.current) return;
    const label = window.prompt('Name this layout version', 'My layout'); if (label === null) return;
    pmRef.current = true; setPmBusy(true);
    try {
      await axios.post(`${API}/api/seating/versions/save`, { label: label || 'My layout', saved_by: 'Host' });
      setLayoutMsg('✓ Layout saved to Version History (Optimize tab)');
      window.setTimeout(() => setLayoutMsg(''), 3500);
    } catch (e) { flash('Couldn’t save the layout — try again.'); }
    pmRef.current = false; setPmBusy(false);
  };
  // Clear All — manual host control. Backend snapshots first (undo/restore) and never deletes guests.
  const clearPlan = async (scope) => {
    if (pmRef.current) return;
    pmRef.current = true; setPmBusy(true);
    try {
      const r = await axios.post(`${API}/api/seating/clear`, { scope, confirm: true });
      setPlan(r.data.plan); setClearOpen(false);
      setLayoutMsg(scope === 'tables'
        ? '✓ Room cleared (tables + fixtures) — Undo, or restore from Version History.'
        : '✓ All seats cleared — Undo, or restore from Version History.');
      window.setTimeout(() => setLayoutMsg(''), 4500);
      await fetchPool();
    } catch (e) { flash('Couldn’t clear — try again.'); }
    pmRef.current = false; setPmBusy(false);
  };

  // 3D-D — capture the live 3D view to a JPEG the host can attach in Communications.
  // We never auto-send: this downloads the image; emailing/texting is always the host's action.
  const snapshot3D = () => {
    const c = document.querySelector('.as-3dwrap canvas');
    if (!c) { setLayoutMsg('Switch to the 3D view first, then snapshot.'); window.setTimeout(() => setLayoutMsg(''), 3500); return; }
    try {
      const url = c.toDataURL('image/jpeg', 0.92);
      const a = document.createElement('a'); a.href = url; a.download = 'seating-3d-snapshot.jpg';
      document.body.appendChild(a); a.click(); a.remove();
      setLayoutMsg('✓ 3D snapshot saved. To email or text it, attach it in Communications — nothing is sent automatically.');
      window.setTimeout(() => setLayoutMsg(''), 6000);
    } catch (e) { setLayoutMsg('Snapshot isn’t available in this browser.'); window.setTimeout(() => setLayoutMsg(''), 3500); }
  };

  const totalSeats = summary.total_seats || 0;
  const guestCount = ps.total || 0;
  const seatedNow = summary.seated || 0;
  const tablesInUse = new Set(assignments.map((a) => a.table_id)).size;
  const capOk = totalSeats >= guestCount;
  const capDelta = totalSeats - guestCount;

  // AI Score — a deterministic 0–100 "how good is this plan" gauge (no LLM). Weighs how many guests
  // are seated, conflict-freedom, accessibility honored, and whether there are enough seats.
  const aiScore = (() => {
    if (!guestCount) return { score: 0, grade: 'Add guests', seatedPct: 0 };
    const seatedPct = Math.min(1, seatedNow / guestCount);
    const conflictFree = totalConflicts === 0 ? 1 : Math.max(0, 1 - totalConflicts / Math.max(1, tables.length));
    const accessOk = accessWarnings.violations.length === 0 ? 1 : 0;
    const capScore = Math.min(1, capOk ? 1 : totalSeats / Math.max(1, guestCount));
    const score = Math.round((0.45 * seatedPct + 0.25 * conflictFree + 0.15 * accessOk + 0.15 * capScore) * 100);
    const grade = score >= 90 ? 'Excellent' : score >= 70 ? 'On track' : score >= 45 ? 'Needs work' : 'Just starting';
    return { score, grade, seatedPct };
  })();

  // Hover detail for each KPI — what the number means + WHO it's about.
  const kpiDetail = (() => {
    const occ = {}; assignments.forEach((a) => { occ[a.table_id] = (occ[a.table_id] || 0) + 1; });
    const seatedIds = new Set(assignments.map((a) => a.guest_id));
    const list = (arr) => (arr.length ? arr.slice(0, 10).join(', ') + (arr.length > 10 ? ` +${arr.length - 10} more` : '') : '—');
    const unseatedNames = poolList.filter((g) => !seatedIds.has(g.id)).map((g) => g.name);
    const confNames = accessWarnings.violations.map((w) => `${w.name} (needs accessible)`);
    return {
      Tables: tables.length ? tables.map((t) => `${t.label || 'Table'} — ${occ[t.id] || 0}/${t.seat_count || 8}`).join(' · ') : 'No tables yet — add some below.',
      Seats: `${summary.seated || 0} taken · ${Math.max(0, totalSeats - (summary.seated || 0))} open (of ${totalSeats})`,
      Guests: poolList.length ? `${poolList.length} confirmed: ${list(poolList.map((g) => g.name))}` : 'No confirmed guests yet.',
      Unseated: unseatedNames.length ? `Still need a seat: ${list(unseatedNames)}` : 'Everyone is seated ✦',
      Conflicts: (conflictInfo.length || confNames.length)
        ? [...conflictInfo.map((c) => `${c.table}: a seated conflict`), ...confNames].join(' · ')
        : 'No conflicts — looking good.',
      'AI Score': guestCount
        ? `${aiScore.grade} (${aiScore.score}/100). Weighs seated (${seatedNow}/${guestCount}), ${totalConflicts === 0 ? 'no conflicts' : `${totalConflicts} conflict${totalConflicts === 1 ? '' : 's'}`}, ${accessWarnings.violations.length === 0 ? 'accessibility honored' : 'an accessibility issue'}, and ${capOk ? 'enough seats' : 'too few seats'}.`
        : 'Add confirmed guests and tables — the score grades how complete and conflict-free the plan is.',
    };
  })();

  if (loading) return <div className="as"><div className="as-loading">Loading the room…</div></div>;

  return (
    <div className="as">
      <div className="as-eb">Planning · The Room</div>
      <div className="as-title">Seating</div>
      <div className="as-meta">{tables.length} table{tables.length === 1 ? '' : 's'} · {totalSeats} seats · {guestCount} guests confirmed</div>
      <div style={{ height: 2, background: 'linear-gradient(90deg, #D4AF37 0%, #F5D061 30%, rgba(212,175,55,0.3) 60%, transparent 100%)', marginTop: 12, marginBottom: 4, boxShadow: '0 1px 4px rgba(212,175,55,0.2)' }} />

      <div className="as-subtabs">
        {[['plan', '▦ Plan'], ['optimize', '✦ Let Milo seat the room'], ['export', '↗ Export']].map(([id, label]) => (
          <button key={id} className={`as-stab ${tab === id ? 'on' : ''}`} onClick={() => setTab(id)}>{label}</button>
        ))}
        <button className="as-btn gold" style={{ marginLeft: 'auto' }} onClick={() => setChartOpen(true)}
          title="Design and print the seating chart">🖨 Seating chart</button>
      </div>

      {tab === 'plan' && (
        <>
          <MiloTips />
          {errMsg && <div className="as-err" role="alert">{errMsg}</div>}
          {(accessWarnings.violations.length > 0 || accessWarnings.noAccessibleButHasNeeds) && (
            <div className="as-accesswarn" role="alert">
              <span className="ic">♿</span>
              <div className="bd">
                {accessWarnings.violations.length > 0 && (
                  <div><b>{accessWarnings.violations.length} accessibility {accessWarnings.violations.length === 1 ? 'conflict' : 'conflicts'}:</b> {accessWarnings.violations.slice(0, 4).map((w) => `${w.name} → ${w.table}`).join(' · ')}{accessWarnings.violations.length > 4 ? ' …' : ''}. Move {accessWarnings.violations.length === 1 ? 'this guest' : 'these guests'} to an accessible table (tap the table → ♿ Accessible).</div>
                )}
                {accessWarnings.noAccessibleButHasNeeds && (
                  <div>{accessWarnings.needsTotal} guest{accessWarnings.needsTotal === 1 ? '' : 's'} {accessWarnings.needsTotal === 1 ? 'has' : 'have'} accessibility needs, but no table is marked <b>♿ Accessible</b> — mark one in the table editor so seating can honor it.</div>
                )}
              </div>
            </div>
          )}
          <div className="as-kpis">
            <div className="as-kpi as-kpihero" tabIndex={0}>
              <KpiRing pct={(aiScore.score || 0) / 100} size={58} stroke={5}><span className="as-scorev">{aiScore.score}</span></KpiRing>
              <div className="as-herometa"><div className="k">AI Score</div><div className="gr">{aiScore.grade}</div></div>
              <div className="as-kpipop">{kpiDetail['AI Score']}</div>
            </div>
            {[
              { k: 'Tables', v: summary.table_count || 0, pct: tables.length ? tablesInUse / tables.length : 0, tone: 'gold' },
              { k: 'Seats', v: totalSeats, pct: totalSeats ? seatedNow / totalSeats : 0, tone: 'gold' },
              { k: 'Guests', v: guestCount, pct: guestCount ? seatedNow / guestCount : 0, tone: 'gold' },
              { k: 'Unseated', v: Math.max(0, guestCount - seatedNow), pct: guestCount ? (guestCount - seatedNow) / guestCount : 0, tone: (guestCount - seatedNow) > 0 ? 'red' : 'green', red: (guestCount - seatedNow) > 0 },
              { k: 'Conflicts', v: totalConflicts, pct: totalConflicts > 0 ? Math.min(1, totalConflicts / Math.max(1, tables.length)) : 0, tone: totalConflicts > 0 ? 'red' : 'green', red: totalConflicts > 0 },
            ].map(({ k, v, pct, tone, red }) => (
              <div className={`as-kpi ${red ? 'red' : ''}`} key={k} tabIndex={0}>
                <KpiRing pct={pct} tone={tone} size={48} stroke={3.5}><span className="v">{v}</span></KpiRing>
                <div className="k">{k}</div>
                <div className="as-kpipop">{kpiDetail[k]}</div>
              </div>
            ))}
          </div>

          <div className={`as-cap ${capOk ? 'ok' : 'short'}`}>
            <div className="as-capbar"><div className="fill" style={{ width: `${guestCount ? Math.min(100, (totalSeats / guestCount) * 100) : (totalSeats ? 100 : 0)}%` }} /></div>
            <div className="as-captext">
              {guestCount === 0 ? 'No confirmed guests yet — seats fill as RSVPs come in.'
                : capOk ? `${totalSeats} seats for ${guestCount} confirmed guests · ${capDelta} to spare`
                  : `Short by ${Math.abs(capDelta)} seats — add about ${Math.ceil(Math.abs(capDelta) / 8)} more table(s).`}
            </div>
          </div>

          {pool && (
            <div className="as-poolcard">
              <div className="as-poolhead" onClick={() => setShowGuests((s) => !s)}>
                <div><b>{ps.total || 0}</b> guests confirmed <span className="sub">(from RSVP · {ps.adults || 0} adults · {ps.children || 0} children)</span></div>
                <div className="as-poolflags">
                  {ps.parties ? <span>{ps.parties} households</span> : null}
                  {ps.with_conflicts ? <span className="warn">{ps.with_conflicts} conflict{ps.with_conflicts === 1 ? '' : 's'}</span> : null}
                  {ps.with_accessibility ? <span>{ps.with_accessibility} accessibility</span> : null}
                  {ps.with_dietary ? <span>{ps.with_dietary} dietary</span> : null}
                  <span className="chev">{showGuests ? '▴' : '▾'}</span>
                </div>
              </div>
              {showGuests && (
                <div className="as-poollist">
                  {(pool.pool || []).length === 0 ? <div className="as-poolempty">No confirmed guests yet — they appear here once RSVPs say yes.</div>
                    : (pool.pool || []).map((g) => (
                      <div className="as-prow" key={g.id}>
                        <span className="nm">{g.name}</span>
                        <span className="tags">
                          {g.age_group && /child|kid|infant/i.test(g.age_group) ? <span className="tag kid">child</span> : null}
                          {g.conflicts_with && g.conflicts_with.length ? <span className="tag warn">conflict</span> : null}
                          {g.accessibility_needs ? <span className="tag" title="Accessibility need">♿</span> : null}
                          {g.dietary_requirements ? <span className="tag" title={g.dietary_requirements}>🍽</span> : null}
                          {g.table_name ? <span className="tag at">{g.table_name}</span> : null}
                        </span>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}

          {conflictCount > 0 && (
            <div className="as-conflictbar" role="alert">⚠ {conflictCount} table{conflictCount > 1 ? 's have' : ' has'} a seating conflict ({conflictInfo.slice(0, 2).map((c) => c.table).join(', ')}{conflictCount > 2 ? '…' : ''}) — two guests marked as conflicting are seated together. Move one on the floor plan.</div>
          )}
          {staleSeats.length > 0 && (
            <div className="as-conflictbar" role="alert">
              ⚠ {staleSeats.length === 1
                ? `A seat at ${staleSeats[0].table} is still held by a guest who is no longer attending`
                : `${staleSeats.length} seats (${staleSeats.slice(0, 3).map((s) => s.table).join(', ')}${staleSeats.length > 3 ? '…' : ''}) are still held by guests who are no longer attending`}
              {' '}— invisible on the floor plan and printed as “Guest” on rosters.
              <button className="as-btn" style={{ padding: '4px 11px', marginLeft: 10 }} disabled={pmBusy}
                onClick={() => staleSeats.forEach((s) => unseatGuest(s.gid))}>
                Release {staleSeats.length === 1 ? 'the seat' : 'these seats'}
              </button>
            </div>
          )}
          {tables.length > 0 && (() => {
            const full = floorFull;
            const floorUI = (
            <>
              <div className="as-seclab" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', ...(full ? { marginTop: 0 } : null) }}>Floor plan
                <span className="as-segmini">
                  <button className={`sm ${viewMode === '2d' ? 'on' : ''}`} onClick={() => setViewMode('2d')} title="Seat guests, arrange tables">2D · Seat &amp; arrange</button>
                  <button className={`sm ${viewMode === '3d' ? 'on' : ''}`} onClick={() => setViewMode('3d')} title="Walkthrough preview">3D · Preview</button>
                </span>
                <button className={`as-btn ${floorLocked ? 'gold' : ''}`} style={{ padding: '5px 11px' }}
                  onClick={() => setFloorLocked((v) => !v)} aria-pressed={floorLocked}
                  title="Lock the room while presenting — nothing can be moved; orbit, pan and zoom stay live">
                  {floorLocked ? '🔒 Locked' : '🔓 Lock'}
                </button>
                {!full && (
                  <button className="as-btn" style={{ padding: '5px 11px' }} onClick={() => setFloorFull(true)}
                    title="Show only the floor plan, as large as the screen allows">⛶ Full view</button>
                )}
                {!full && <button className="as-btn" style={{ padding: '5px 11px' }} disabled={pmBusy} onClick={saveLayout}>⛁ Save layout</button>}
                {!full && <button className="as-btn" style={{ padding: '5px 11px' }} disabled={pmBusy} onClick={() => setClearOpen(true)}>⌫ Clear</button>}
                {!full && <button className="as-btn" style={{ padding: '5px 11px', marginLeft: 'auto' }} onClick={() => setConflictOpen(true)}>⚡ Mark conflict</button>}
                {!full && <button className="as-btn" style={{ padding: '5px 11px' }} onClick={() => setGroupsOpen(true)}>◓ Relationship groups{relGroups.length ? ` · ${relGroups.length}` : ''}</button>}
                {full && <button className="as-btn gold" style={{ padding: '5px 13px', marginLeft: 'auto' }} onClick={() => setFloorFull(false)}>✕ Exit full view (Esc)</button>}
              </div>
              {!full && layoutMsg && <div className="as-savedmsg">{layoutMsg}</div>}
              {!full && <div className="as-fixbar">
                <button className={`as-fixtoggle ${fixOpen ? 'on' : ''}`} aria-expanded={fixOpen} onClick={() => setFixOpen((o) => !o)}>
                  ＋ Add to the room <span className="chev">{fixOpen ? '▴' : '▾'}</span>
                </button>
                {fixOpen && (
                  <div className="as-fixpanel">
                    {FIXTURE_TYPES.map((f) => (
                      <React.Fragment key={f.t}>
                        {f.t === 'cake' ? <span className="as-fixdiv2" /> : null}
                        <button className="as-fix" disabled={pmBusy} onClick={() => addFixture(f.t)}>{f.ic} {f.l}</button>
                      </React.Fragment>
                    ))}
                  </div>
                )}
                {viewMode === '3d' && (
                  <>
                    <span className="as-fixdiv" />
                    <label className="as-fix as-fixup" aria-disabled={pmBusy}>🖼 {floorImage ? 'Replace venue photo' : 'Venue photo'}
                      <input type="file" accept="image/*" hidden disabled={pmBusy} onChange={(e) => { pickPhoto(e.target.files && e.target.files[0]); e.target.value = ''; }} />
                    </label>
                    {floorImage && <button className="as-fix" disabled={pmBusy} onClick={removePhoto}>✕ Remove photo</button>}
                    <span className="as-fixdiv" />
                    <button className="as-fix" onClick={snapshot3D}>📸 Snapshot</button>
                  </>
                )}
              </div>}
              {viewMode === '2d'
                ? <FloorPlanCanvas
                    tables={tables} assignments={assignments} poolList={poolList} groupColor={groupColor}
                    relGroups={relGroups}
                    fixtures={fixtures} onMoveTable={moveTable} onMoveFixture={moveFixture} onRemoveFixture={removeFixture} onEditFixture={(f) => setEditingFix(f)}
                    onAssign={assignGuest} onSeatGroup={seatGroup} onUnseat={unseatGuest} onProtect={protectGuest}
                    onEditTable={(t) => setEditing({ table: t, isNew: false })}
                    selectedIds={selectedIds} onToggleSelect={toggleSelect} onClearSelection={clearSelection}
                    onRequestMove={requestMove} onPrintRoster={() => setChartOpen(true)} onEmailRoster={shareRosterViaComms}
                    onAssignSeat={assignSeat} onSwapSeats={swapSeats}
                    locked={floorLocked}
                    stageHeight={full ? Math.max(560, (typeof window !== 'undefined' ? window.innerHeight : 900) - 128) : undefined}
                  />
                : <>
                    {!full && <div className="as-3dnote" role="note">
                      <span className="ic">✦</span>
                      {selectedIds.length > 0
                        ? <><span><b>{selectedIds.length} selected</b> — click a table to move them here (Milo warns on conflicts).</span>
                            <button className="as-btn" style={{ padding: '5px 11px', marginLeft: 'auto' }} onClick={clearSelection}>Clear</button></>
                        : <span>Drag a guest to any chair — an empty chair moves them, an occupied chair swaps the two guests. Drag tables &amp; landmarks to arrange · drag empty floor to orbit · scroll to zoom.</span>}
                    </div>}
                    <div className="as-3dwrap" role="application" style={full ? { height: 'calc(100vh - 128px)' } : undefined}
                      aria-label={floorLocked ? 'Interactive 3D floor plan — locked, view only. Drag anywhere to orbit.' : "Interactive 3D floor plan. Click a guest's seat to select them, then click a table to move them there."}>
                      <React.Suspense fallback={<div className="as-3dloading">Preparing the 3D room…</div>}>
                        <Floor3D tables={tables} assignments={assignments} fixtures={fixtures} floorImage={floorImage} poolList={poolList}
                          onMoveTable={moveTable} onMoveFixture={moveFixture} onRemoveFixture={removeFixture} onEditFixture={(f) => setEditingFix(f)}
                          onEditTable={(t) => setEditing({ table: t, isNew: false })}
                          selectedIds={selectedIds} onToggleSelect={toggleSelect} onClearSelection={clearSelection} onRequestMove={requestMove}
                          onAssignSeat={assignSeat} onSwapSeats={swapSeats} locked={floorLocked} />
                      </React.Suspense>
                    </div>
                    {floorLocked && <div className="as-cardhint" style={{ marginTop: 6 }}>🔒 Locked — drag anywhere to orbit, scroll to zoom. Nothing can be moved until you unlock.</div>}
                  </>}
              {!full && fixtures.length > 0 && (
                <div className="as-fixlist">{fixtures.map((f) => <span key={f.id} className="as-fixchip">{f.label || f.type}<button title="Remove" onClick={() => removeFixture(f.id)}>✕</button></span>)}</div>
              )}
              {!full && <div className="as-cardhint">{viewMode === '3d'
                ? 'Drag a guest onto any chair — empty chair moves them, occupied chair swaps the two guests (Milo warns on conflicts) · a quick tap still picks a guest up; tap a table to move them · drag a table or landmark to move it · drag empty floor to orbit · scroll to zoom. To edit a table, tap it with no one selected.'
                : 'Each guest sits on a named seat. Drag a guest’s seat onto another table to move them (Milo warns on conflicts). Drag an unseated guest onto a table to seat them · ✓ to multi-select · scroll to zoom · tap a table to edit.'}</div>}
            </>
            );
            return full ? createPortal(<div className="as-floorfull">{floorUI}</div>, document.body) : floorUI;
          })()}

          {tables.length > 0 && <MiloConcierge tables={tables} assignments={assignments} poolList={poolList} relGroups={relGroups} onRequestMove={requestMove}
            ghostSeats={(plan && plan.ghost_seats) || []} onReleaseGhosts={releaseGhosts} />}

          <div className="as-seclab">Tables</div>
          {tables.length === 0 ? (
            <div className="as-quick">
              <div className="qt">Set up the room</div>
              <div className="qd">Create all your tables in one step, then fine-tune each — or add them one at a time. (Children take a full seat.)</div>
              <div className="as-row">
                <button className="as-btn gold" onClick={() => setBulkOpen(true)}>＋ Quick-create tables</button>
                <button className="as-btn" onClick={() => setEditing({ table: BLANK_TABLE(), isNew: true })}>Add one table</button>
              </div>
            </div>
          ) : (
            <>
              <div className="as-tgrid">
                {tables.map((t) => {
                  const seatArr = seatMapMain[t.id] || [];
                  const occupants = seatArr.filter(Boolean).map((gid) => guestsById[gid]).filter(Boolean);   // chair order = families together
                  const MAXCHIPS = 6;
                  return (
                    <div className={`as-tcard st-${t.status || 'standard'} ${seatFocusTid === t.id ? 'focus' : ''}`} key={t.id} onClick={() => setEditing({ table: t, isNew: false })}>
                      <div className="as-tic">{SHAPE_ICON[t.shape] || '◯'}</div>
                      <div className="as-tmain">
                        <div className="as-tname">{t.label || 'Untitled table'}</div>
                        <div className="as-tmeta">
                          <span>{occupants.length}/{t.seat_count} seated</span>
                          {t.status && t.status !== 'standard' ? <span className={`as-stchip ${t.status}`}>{t.status}</span> : null}
                          {t.personality ? <span className="as-pers">· {t.personality}</span> : null}
                        </div>
                        {occupants.length > 0 && (
                          <div className="as-toccs" title={occupants.map((g) => g.name).join(', ')}>
                            {occupants.slice(0, MAXCHIPS).map((g) => (
                              <span className="as-tocc" key={g.id}>
                                <span className="dot" style={{ background: groupColor[g.id] || 'var(--as-gold)' }} />{(g.name || '').trim().split(/\s+/)[0]}
                              </span>
                            ))}
                            {occupants.length > MAXCHIPS && <span className="as-tocc more">+{occupants.length - MAXCHIPS} more</span>}
                          </div>
                        )}
                      </div>
                      <div className="as-ttools" onClick={(e) => e.stopPropagation()}>
                        <button className="as-tool" title="Assign seats — open this table's chairs" onClick={() => setSeatFocusTid((p) => (p === t.id ? null : t.id))}>🪑</button>
                        <button className="as-tool" title="Seat guests here (pick several at once)" onClick={() => setBulkSeatT(t)}>＋👥</button>
                        <button className="as-tool" title="Edit" onClick={() => setEditing({ table: t, isNew: false })}>✎</button>
                        <button className="as-tool del" title="Delete" onClick={() => setConfirmDel(t)}>🗑</button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {seatFocusTid && tables.some((t) => t.id === seatFocusTid) && (
                <div className="as-seatfocus">
                  <div className="as-seatfocushead">
                    <span className="t">🪑 Chair-by-chair — {(tables.find((t) => t.id === seatFocusTid) || {}).label || 'Table'}</span>
                    <span className="d">Drag a guest between chairs — an occupied chair swaps the two guests.</span>
                    <button className="as-btn" onClick={() => setSeatFocusTid(null)}>✕ Close</button>
                  </div>
                  <SeatGridView tables={tables} assignments={assignments} poolList={poolList} groupColor={groupColor} relGroups={relGroups}
                    onAssignSeat={assignSeat} onSwapSeats={swapSeats} onUnseat={unseatGuest}
                    selectedIds={selectedIds} onToggleSelect={toggleSelect} onClearSelection={clearSelection} onRequestMove={requestMove}
                    onlyTableId={seatFocusTid} />
                </div>
              )}
              <div className="as-row" style={{ marginTop: 14 }}>
                <button className="as-btn" onClick={() => setEditing({ table: BLANK_TABLE(), isNew: true })}>＋ Add a table</button>
                <button className="as-btn" onClick={() => setBulkOpen(true)}>⊞ Bulk add</button>
              </div>
            </>
          )}

          <div className="as-note">Manual setup, full control — every table editable and deletable. Next: the visual floor plan + drag-to-seat, then the grounded optimizer. Flag-gated, local.</div>
        </>
      )}

      {tab === 'optimize' && <OptimizeView plan={plan} poolList={poolList} onChange={refreshSilent} />}
      {tab === 'export' && <ExportView plan={plan} poolList={poolList} onOpenDesigner={() => setChartOpen(true)} />}

      <ChartDesigner open={chartOpen} onClose={() => setChartOpen(false)} plan={plan} poolList={poolList} onSaveDesign={saveChartDesign} />

      {bulkSeatT && (
        <BulkSeatPicker table={bulkSeatT} poolList={poolList} assignments={assignments}
          onClose={() => setBulkSeatT(null)}
          onConfirm={async (ids) => { setBulkSeatT(null); await seatGroup(ids, bulkSeatT.id); }} />
      )}

      {editing && (
        <TableEditor
          initial={editing.table} isNew={editing.isNew} saving={saving}
          occupied={editing.table.id ? assignments.filter((a) => a.table_id === editing.table.id).length : 0}
          onClose={() => setEditing(null)} onSave={saveTable}
          onDelete={editing.isNew ? null : () => { setConfirmDel(editing.table); setEditing(null); }}
        />
      )}
      {bulkOpen && <BulkEditor saving={saving} startCount={tables.length} onClose={() => setBulkOpen(false)} onCreate={bulkAdd} />}
      {groupsOpen && <GroupsManager groups={relGroups} poolList={poolList} onClose={() => setGroupsOpen(false)} onSave={(g) => { setGroupsOpen(false); saveGroups(g); }} />}
      {conflictOpen && <ConflictPicker poolList={poolList} onClose={() => setConflictOpen(false)} onMark={markConflict} />}
      {moveReq && <MoveResolver req={moveReq} tables={tables} assignments={assignments} poolList={poolList} onCancel={() => setMoveReq(null)} onApply={applyMove} />}
      {confirmDel && (
        <div className="as-ov" onClick={(e) => { if (e.target === e.currentTarget) setConfirmDel(null); }}>
          <div className="as-modal" style={{ maxWidth: 420 }}>
            <div className="as-mhead"><div className="mt">Delete this table?</div><button className="as-mx" onClick={() => setConfirmDel(null)}>✕</button></div>
            <div className="as-mbody"><div className="as-confirm">Remove <b>“{confirmDel.label || 'Untitled table'}”</b>. Any guests seated here are returned to unseated.</div></div>
            <div className="as-mfoot"><button className="as-btn" onClick={() => setConfirmDel(null)}>Keep it</button><button className="as-btn danger" onClick={() => removeTable(confirmDel)}>Delete table</button></div>
          </div>
        </div>
      )}
      {clearOpen && (
        <div className="as-ov" onClick={(e) => { if (e.target === e.currentTarget) setClearOpen(false); }}>
          <div className="as-modal" style={{ maxWidth: 460 }}>
            <div className="as-mhead"><div className="mt">Clear the plan?</div><button className="as-mx" onClick={() => setClearOpen(false)}>✕</button></div>
            <div className="as-mbody">
              <div className="as-confirm">A restore point is saved first, so you can <b>Undo</b> this or bring it back from <b>Version History</b>. Your guest list is never touched.</div>
              <div className="as-clearopts">
                <button className="as-btn danger" disabled={pmBusy} onClick={() => clearPlan('assignments')}>⌫ Clear all seats <small>(tables &amp; fixtures stay)</small></button>
                <button className="as-btn danger ghost" disabled={pmBusy} onClick={() => clearPlan('tables')}>⌫ Clear entire room <small>(also removes tables &amp; fixtures)</small></button>
              </div>
            </div>
            <div className="as-mfoot"><button className="as-btn" onClick={() => setClearOpen(false)}>Cancel</button></div>
          </div>
        </div>
      )}
      {editingFix && (
        <FixtureEditor initial={editingFix} onClose={() => setEditingFix(null)} onSave={saveFixtureEdit}
          onDelete={() => { removeFixture(editingFix.id); setEditingFix(null); }} />
      )}
    </div>
  );
}

function FixtureEditor({ initial, onClose, onSave, onDelete }) {
  useEscClose(onClose);
  const [f, setF] = useState({ ...initial });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const isCircle = f.shape === 'circle';
  return (
    <div className="as-ov" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="as-modal" style={{ maxWidth: 420 }}>
        <div className="as-mhead"><div className="mt">Edit {(f.label || 'feature')}</div><button className="as-mx" onClick={onClose}>✕</button></div>
        <div className="as-mbody">
          <div className="as-fld"><label>Label</label><input className="as-inp" value={f.label || ''} onChange={(e) => set('label', e.target.value)} autoFocus /></div>
          <div className="as-row2">
            {isCircle
              ? <div className="as-fld" style={{ gridColumn: '1 / -1' }}><label>Diameter (ft)</label><input className="as-inp" type="number" min="1" max="60" step="0.5" value={pxToFt(f.w)} onChange={(e) => { const px = ftToPx(e.target.value); set('w', px); set('h', px); }} /></div>
              : <>
                  <div className="as-fld"><label>Width (ft)</label><input className="as-inp" type="number" min="1" max="80" step="0.5" value={pxToFt(f.w)} onChange={(e) => set('w', ftToPx(e.target.value))} /></div>
                  <div className="as-fld"><label>Length (ft)</label><input className="as-inp" type="number" min="1" max="80" step="0.5" value={pxToFt(f.h)} onChange={(e) => set('h', ftToPx(e.target.value))} /></div>
                </>}
          </div>
          <div className="as-fld"><label>Rotation <small>{Math.round(f.rotation || 0)}°</small></label>
            <input type="range" className="as-range" min="0" max="350" step="10" value={Math.round(f.rotation || 0)} onChange={(e) => set('rotation', Number(e.target.value))} />
          </div>
        </div>
        <div className="as-mfoot">
          <button className="as-btn danger ghost" onClick={onDelete}>Delete</button>
          <button className="as-btn" onClick={onClose}>Cancel</button>
          <button className="as-btn gold" onClick={() => onSave(f)}>Save</button>
        </div>
      </div>
    </div>
  );
}

function TableEditor({ initial, isNew, saving, occupied = 0, onClose, onSave, onDelete }) {
  useEscClose(onClose);
  const [f, setF] = useState({ ...BLANK_TABLE(), ...initial });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const cap = Number(f.seat_count) || 0;
  const fillPct = cap ? Math.min(100, Math.round((occupied / cap) * 100)) : 0;
  const fillState = !cap ? '' : occupied >= cap ? 'full' : occupied === 0 ? 'empty' : fillPct >= 75 ? 'high' : '';
  return (
    <div className="as-ov" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="as-modal">
        <div className="as-mhead"><div className="mt">{isNew ? 'Add a table' : 'Edit table'}</div><button className="as-mx" onClick={onClose}>✕</button></div>
        <div className="as-mbody">
          {!isNew && (
            <div className={`as-fillrow ${fillState}`}>
              <div className="as-filltrack"><div className="as-fillbar" style={{ width: `${fillPct}%` }} /></div>
              <div className="as-filltext">{occupied >= cap && cap ? 'Full' : `${occupied} of ${cap} seats`}<span> · {fillPct}%</span></div>
            </div>
          )}
          <div className="as-fld"><label>Name</label><input className="as-inp" value={f.label} onChange={(e) => set('label', e.target.value)} placeholder="e.g. Magnolia, or Table 1" autoFocus /></div>
          <div className="as-row2">
            <div className="as-fld"><label>Shape</label>
              <select className="as-sel" value={f.shape} onChange={(e) => set('shape', e.target.value)}>{SHAPES.map((s) => <option key={s} value={s}>{SHAPE_LABEL[s]}</option>)}</select>
            </div>
            <div className="as-fld"><label>Seats</label><input className="as-inp" type="number" min="1" max="20" value={f.seat_count} onChange={(e) => set('seat_count', Number(e.target.value) || 0)} /></div>
          </div>
          <div className="as-row2">
            {['rectangle', 'sweetheart', 'head'].includes(f.shape) ? (
              <>
                <div className="as-fld"><label>Width (ft)</label><input className="as-inp" type="number" min="2" max="40" step="0.5" value={pxToFt(f.w)} placeholder={String(derivedDiaFt(f.seat_count))} onChange={(e) => set('w', ftToPx(e.target.value))} /></div>
                <div className="as-fld"><label>Length (ft)</label><input className="as-inp" type="number" min="2" max="40" step="0.5" value={pxToFt(f.h)} placeholder={String(Math.round(derivedDiaFt(f.seat_count) / 2))} onChange={(e) => set('h', ftToPx(e.target.value))} /></div>
              </>
            ) : (
              <div className="as-fld" style={{ gridColumn: '1 / -1' }}><label>Diameter (ft) <small>blank = auto-fit to seats</small></label><input className="as-inp" type="number" min="2" max="30" step="0.5" value={pxToFt(f.w)} placeholder={`${derivedDiaFt(f.seat_count)} (auto)`} onChange={(e) => { const px = ftToPx(e.target.value); set('w', px); set('h', px); }} /></div>
            )}
          </div>
          <div className="as-row2">
            <div className="as-fld"><label>Table type <small>(its role)</small></label>
              <select className="as-sel" value={f.status} onChange={(e) => set('status', e.target.value)}>{STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}</select>
            </div>
            <div className="as-fld"><label>Best suited for <small>(guides seating)</small></label>
              <select className="as-sel" value={f.personality || ''} onChange={(e) => set('personality', e.target.value)}>{PERSONALITY_OPTS.map(([v, l]) => <option key={v || 'none'} value={v}>{l}</option>)}</select>
            </div>
          </div>
          <label className="as-check"><input type="checkbox" checked={!!f.accessible} onChange={(e) => set('accessible', e.target.checked)} /> <span>♿ Accessible table <small>(near ramp / exit — flagged on the floor plan; the seater prefers it for guests with accessibility needs)</small></span></label>
          <div className="as-fld"><label>Notes <small>(optional)</small></label><textarea className="as-inp as-area" rows="2" value={f.notes || ''} onChange={(e) => set('notes', e.target.value)} placeholder="e.g. near the dance floor · away from the speakers · grandparents' table" /></div>
        </div>
        <div className="as-mfoot">
          {onDelete && <button className="as-btn danger ghost" onClick={onDelete}>Delete</button>}
          <button className="as-btn" onClick={onClose}>Cancel</button>
          <button className="as-btn gold" disabled={saving} onClick={() => onSave({ ...f, seat_count: Number(f.seat_count) || 1, personality: f.personality || null, accessible: !!f.accessible })}>Save</button>
        </div>
      </div>
    </div>
  );
}

// X6 — Conflict quick-create. Pick two guests who shouldn't sit together; writes conflicts_with on
// both (via the backend, which owns the Guests data). Lists existing conflicts with one-tap removal.
function ConflictPicker({ poolList, onClose, onMark }) {
  useEscClose(onClose);
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const nameById = useMemo(() => { const m = {}; (poolList || []).forEach((g) => { m[g.id] = g.name; }); return m; }, [poolList]);
  const pairs = useMemo(() => {
    const seen = new Set(); const out = [];
    (poolList || []).forEach((g) => (g.conflicts_with || []).forEach((other) => {
      const key = [g.id, other].sort().join('|');
      if (seen.has(key) || !nameById[other]) return; seen.add(key);
      out.push({ a: g.id, b: other });
    }));
    return out;
  }, [poolList, nameById]);
  const opts = (poolList || []).slice().sort((x, y) => (x.name || '').localeCompare(y.name || ''));
  const canMark = a && b && a !== b;
  return (
    <div className="as-ov" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="as-modal" style={{ maxWidth: 470 }}>
        <div className="as-mhead"><div className="mt">Mark a conflict</div><button className="as-mx" onClick={onClose}>✕</button></div>
        <div className="as-mbody">
          <div className="as-confirm" style={{ marginBottom: 6 }}>Pick two guests who <b>shouldn’t sit together</b> — the seater and Milo will keep them at different tables.</div>
          <div className="as-row2">
            <div className="as-fld"><label>Guest</label>
              <select className="as-sel" value={a} onChange={(e) => setA(e.target.value)}><option value="">Choose…</option>{opts.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}</select>
            </div>
            <div className="as-fld"><label>Conflicts with</label>
              <select className="as-sel" value={b} onChange={(e) => setB(e.target.value)}><option value="">Choose…</option>{opts.filter((g) => g.id !== a).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}</select>
            </div>
          </div>
          <button className="as-btn gold" disabled={!canMark} style={{ marginTop: 6 }} onClick={() => { onMark(a, b, 'add'); setA(''); setB(''); }}>⚡ Mark these two as a conflict</button>
          <div className="as-seclab" style={{ marginTop: 18, marginLeft: 0 }}>Current conflicts</div>
          {pairs.length === 0
            ? <div className="as-poolempty">No conflicts marked yet.</div>
            : <div className="as-conflist">{pairs.map((p) => (
                <div className="as-confrow" key={`${p.a}|${p.b}`}>
                  <span>{nameById[p.a]} <span className="cx">⚡</span> {nameById[p.b]}</span>
                  <button className="as-confx" title="Remove this conflict" onClick={() => onMark(p.a, p.b, 'remove')}>✕</button>
                </div>
              ))}</div>}
        </div>
        <div className="as-mfoot"><button className="as-btn" onClick={onClose}>Done</button></div>
      </div>
    </div>
  );
}

function BulkEditor({ saving, startCount, onClose, onCreate }) {
  useEscClose(onClose);
  const [count, setCount] = useState(10);
  const [seats, setSeats] = useState(8);
  const [shape, setShape] = useState('round');
  const [prefix, setPrefix] = useState('Table');
  const n = Number(count) || 1;
  return (
    <div className="as-ov" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="as-modal">
        <div className="as-mhead"><div className="mt">Quick-create tables</div><button className="as-mx" onClick={onClose}>✕</button></div>
        <div className="as-mbody">
          <div className="as-row2">
            <div className="as-fld"><label>How many</label><input className="as-inp" type="number" min="1" max="50" value={count} onChange={(e) => setCount(e.target.value)} autoFocus /></div>
            <div className="as-fld"><label>Seats each</label><input className="as-inp" type="number" min="1" max="20" value={seats} onChange={(e) => setSeats(e.target.value)} /></div>
          </div>
          <div className="as-fld"><label>Shape</label>
            <div className="as-chiprow">{SHAPES.map((s) => <span key={s} className={`as-pick ${shape === s ? 'on' : ''}`} onClick={() => setShape(s)}>{SHAPE_ICON[s]} {SHAPE_LABEL[s]}</span>)}</div>
          </div>
          <div className="as-fld"><label>Name prefix</label><input className="as-inp" value={prefix} onChange={(e) => setPrefix(e.target.value)} placeholder="Table" /><div className="as-fhint">Creates “{prefix || 'Table'} {startCount + 1}” … “{prefix || 'Table'} {startCount + n}”.</div></div>
        </div>
        <div className="as-mfoot"><button className="as-btn" onClick={onClose}>Cancel</button><button className="as-btn gold" disabled={saving} onClick={() => onCreate({ count, seat_count: seats, shape, name_prefix: prefix })}>Create {n} table{n === 1 ? '' : 's'}</button></div>
      </div>
    </div>
  );
}

/* ---- Optimize: Milo seats the room — Generate Draft → review → apply → undo + version history ---- */
function OptimizeView({ plan, poolList, onChange }) {
  const [proposal, setProposal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [versions, setVersions] = useState([]);
  const [undoId, setUndoId] = useState((plan && plan.undo_version_id) || null);
  const [why, setWhy] = useState(false);      // X8 — collapsible "Why?" rationale on the AI action card
  const [applied, setApplied] = useState(null); // captured rationale of the last applied draft (for the undo card)
  const [ackBlocking, setAckBlocking] = useState(false); // Review-Queue gate: blocking issues must be acknowledged before Apply
  const namesById = useMemo(() => { const m = {}; (poolList || []).forEach((g) => { m[g.id] = g.name; }); return m; }, [poolList]);
  // Short, plain-language reasons for a proposal — powers the "Why?" reveal before AND after apply.
  const summarize = (pp) => {
    if (!pp) return [];
    const out = [];
    out.push((pp.unseated || []).length === 0
      ? `Everyone seated (${pp.seated}/${pp.total}).`
      : `${pp.unseated.length} couldn’t be seated: ${(pp.unseated).slice(0, 3).map((u) => namesById[u.guest_id] || u.guest_id).join(', ')}${pp.unseated.length > 3 ? '…' : ''}.`);
    out.push((pp.conflicts || []).length === 0 ? 'No conflicting guests seated together.' : `${pp.conflicts.length} table(s) need a look for conflicts.`);
    if (pp.units) out.push(`${pp.units.kept_whole} family/parties kept whole${pp.units.split ? `, ${pp.units.split} split (capacity)` : ''}.`);
    (pp.split_warnings || []).slice(0, 3).forEach((w) => out.push(`${w.label} split across ${w.tables.length} table(s) — ${w.reason}.`));
    if ((pp.accessibility_warnings || []).length > 0) out.push(`${pp.accessibility_warnings.length} accessibility warning(s) — guests not at an accessible table.`);
    Object.values(pp.per_table || {}).filter((t) => (t.notes || []).length).slice(0, 5).forEach((t) => out.push(`${t.label}: ${t.notes.join(' · ')}`));
    return out;
  };

  const loadVersions = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/seating/versions`); setVersions(r.data.versions || []); } catch (e) { /* */ }
  }, []);
  useEffect(() => { loadVersions(); }, [loadVersions]);

  const generate = async () => {
    setBusy(true); setErr(''); setAckBlocking(false);
    try { const r = await axios.post(`${API}/api/seating/optimize`, { seed: 42 }); setProposal(r.data.proposal); }
    catch (e) { setErr((e && e.response && e.response.data && e.response.data.detail) || 'Could not generate a draft.'); }
    setBusy(false);
  };
  const apply = async () => {
    if (!proposal) return;
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${API}/api/seating/optimize/apply`, { confirm: true, assignments: proposal.assignments });
      setApplied({ seated: proposal.seated, total: proposal.total, score: proposal.score, method: proposal.method, reasons: summarize(proposal) });
      setWhy(false); setUndoId(r.data.undo_version_id); setProposal(null); await onChange(); await loadVersions();
    } catch (e) {
      if (e && e.response && e.response.status === 403) setErr('Milo actions are turned off. Enable them in AI settings, then apply.');
      else setErr((e && e.response && e.response.data && e.response.data.detail) || 'Could not apply.');
    }
    setBusy(false);
  };
  const undo = async () => {
    setBusy(true);
    try { await axios.post(`${API}/api/seating/optimize/undo`); setUndoId(null); await onChange(); await loadVersions(); } catch (e) { /* */ }
    setBusy(false);
  };
  const saveVersion = async () => {
    const label = window.prompt('Name this version', 'Saved plan'); if (label === null) return;
    setBusy(true);
    try { await axios.post(`${API}/api/seating/versions/save`, { label: label || 'Saved plan', saved_by: 'Host' }); await loadVersions(); } catch (e) { /* */ }
    setBusy(false);
  };
  const restore = async (v) => {
    setBusy(true);
    try { await axios.post(`${API}/api/seating/versions/${v.id}/restore`, { confirm: true }); setUndoId(null); await onChange(); await loadVersions(); } catch (e) { /* */ }
    setBusy(false);
  };
  const deleteVersion = async (v) => {
    if (!window.confirm(`Delete the version “${v.label}”? This can’t be undone.`)) return;
    setBusy(true);
    try { await axios.delete(`${API}/api/seating/versions/${v.id}`); await loadVersions(); } catch (e) { /* */ }
    setBusy(false);
  };

  const p = proposal;
  const dash = p ? Math.round(327 * (1 - (p.score || 0) / 100)) : 327;
  const blockingCount = p ? (p.review_items || []).filter((r) => r.severity === 'blocking').length : 0;
  // Phase-1: Data vs Seating confidence + cause-tagged Milo review
  const confMeta = (lvl) => ({ high: { c: '#789A82', t: 'High' }, medium: { c: '#C6A75A', t: 'Medium' }, low: { c: '#B53434', t: 'Low' } }[lvl] || { c: '#988F80', t: '—' });
  const causeMeta = { DATA: 'Data', REQUIREMENTS: 'Conflict', SEATING_LIMIT: 'Capacity', SOLVER: 'Choice' };

  return (
    <div>
      {/* Mock v3 (host-approved 2026-07-14): ONE double-bezel card holds both ways to ask Milo —
          the one-click draft and the tell-him-in-your-own-words line — and, after Milo seats the
          room, the result row (Undo · Revise · Keep) appears INSIDE the same card. No separate
          banner, no jargon lines. */}
      <div className="as-msub">He drafts, you decide — and every step can be undone.</div>
      <div className="as-gov">
        {['Generate draft', 'Review', 'Modify', 'Apply', 'Undo'].map((s, i) => (
          <React.Fragment key={s}>
            <div className={`as-gstep ${(i === 0 || (p && i <= 1)) ? 'on' : ''}`}><b>{i + 1}</b>{s}</div>
            {i < 4 ? <span className="as-garr">→</span> : null}
          </React.Fragment>
        ))}
      </div>

      {err && <div className="as-derr">{err}</div>}

      {!p ? (
        <div className="as-mshell">
          <div className="as-mcard">
            <div className="qd">Milo drafts a complete plan from your guest list — families together, conflicts apart, preferred tables honored. You review it first; nothing changes until you say so.</div>
            <button className="as-btn gold as-btnarrow" disabled={busy} onClick={generate}>
              {busy ? 'Working…' : <>✦ Generate a draft <span className="tip">→</span></>}
            </button>
            <div className="as-mdivider">or</div>
            <MiloChat onOptimize={generate} />
            {/* only after Milo ACTUALLY seated the room in this sitting (host rule 2026-07-14) —
                a leftover undo pointer from an older action must not resurrect this row on load;
                those restore points live in Version History below. */}
            {undoId && applied && (
              <div className="as-mresult">
                <span className="as-aiorb">M</span>
                <div className="as-aitext"><b>✦ Milo seated the room — {applied.seated}/{applied.total} seated.</b> Tweak any seat on the plan, or choose below.</div>
                <div className="as-aiacts">
                  <button className="as-btn" disabled={busy} onClick={() => { setWhy(false); undo(); }}>↶ Undo</button>
                  <button className="as-btn" disabled={busy} onClick={() => { setWhy(false); generate(); }}>✎ Revise</button>
                  <button className="as-btn gold" disabled={busy} onClick={() => { setUndoId(null); setApplied(null); setWhy(false); }}>Keep</button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="as-draftgrid">
          <div className="as-dcard">
            <div className="as-seclab" style={{ marginTop: 0 }}>Proposed plan · draft</div>
            <div className="as-aitext" style={{ marginBottom: 2 }}>
              <b>{(p.unseated || []).length === 0 ? `Everyone seated (${p.seated}/${p.total}).` : `${p.seated}/${p.total} seated.`}</b>{' '}
              {((p.conflicts || []).length === 0 && (p.accessibility_warnings || []).length === 0) ? 'No conflicts.' : 'A few things to review.'}
              {p.method ? ` · ${p.method === 'cp-sat' ? 'exact solver' : 'fast solver'}` : ''}
            </div>
            {(p.seating_confidence || p.data_confidence) && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {[['Seating', p.seating_confidence, 'How well the plan satisfied the rules (engine quality)'], ['Data', p.data_confidence, 'How complete the grouping data is (input quality)']].map(([lab, lvl, hint]) => {
                  const m = confMeta(lvl);
                  return (
                    <span key={lab} title={hint} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: m.c + '14', border: `1px solid ${m.c}55`, color: m.c }}>
                      <span style={{ width: 7, height: 7, borderRadius: 999, background: m.c }} />{lab} confidence: {m.t}
                    </span>
                  );
                })}
                {p.units ? <span style={{ fontSize: 11, color: '#6B6556' }}>{p.units.kept_whole} kept whole{p.units.split ? ` · ${p.units.split} split` : ''}</span> : null}
              </div>
            )}
            {(p.review_items || []).length > 0 && (
              <div style={{ marginTop: 10, border: '1px solid rgba(60,48,20,0.16)', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ padding: '7px 11px', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6556', background: 'rgba(198,167,90,0.08)' }}>✦ Milo review — {p.review_items.filter((r) => r.severity === 'blocking').length} to resolve · {p.review_items.filter((r) => r.severity === 'advisory').length} advisory</div>
                <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                  {[...p.review_items].sort((a, b) => (a.severity === 'blocking' ? 0 : 1) - (b.severity === 'blocking' ? 0 : 1)).slice(0, 30).map((r, i) => {
                    const blocking = r.severity === 'blocking';
                    return (
                      <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 11px', borderTop: i ? '1px solid rgba(60,48,20,0.08)' : 'none' }}>
                        <span title={`Cause: ${r.cause}`} style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, flex: 'none', background: blocking ? 'rgba(181,52,52,0.10)' : 'rgba(120,110,86,0.10)', color: blocking ? '#B53434' : '#6B6556' }}>{causeMeta[r.cause] || r.cause}</span>
                        <span style={{ fontSize: 12.5, color: '#2A2418', lineHeight: 1.4 }}>{r.message}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {(p.accessibility_warnings || []).length > 0 && (
              <div className="as-why warn"><span className="ic">♿</span><span><b>{p.accessibility_warnings.length} accessibility {p.accessibility_warnings.length === 1 ? 'warning' : 'warnings'}</b> — open Why? for detail.</span></div>
            )}
            {why && (
              <div className="as-pwhy">{summarize(p).map((r, i) => <div className="as-why" key={i}><span className="ic">·</span><span>{r}</span></div>)}</div>
            )}
            {blockingCount > 0 && (
              <label className="as-ackgate">
                <input type="checkbox" checked={ackBlocking} onChange={(e) => setAckBlocking(e.target.checked)} />
                <span>I’ve reviewed the <b>{blockingCount}</b> item{blockingCount === 1 ? '' : 's'} that need attention above, and want to apply this plan anyway.</span>
              </label>
            )}
            <div className="as-row" style={{ marginTop: 14 }}>
              <button className="as-btn gold" disabled={busy || (blockingCount > 0 && !ackBlocking)}
                title={blockingCount > 0 && !ackBlocking ? 'Acknowledge the items that need attention first' : undefined}
                onClick={apply}>✓ Accept &amp; apply</button>
              <button className="as-btn" disabled={busy} onClick={() => setWhy((w) => !w)}>{why ? 'Hide' : 'Why?'}</button>
              <button className="as-btn" disabled={busy} onClick={generate}>Re-draft</button>
              <button className="as-btn" disabled={busy} onClick={() => { setProposal(null); setWhy(false); setAckBlocking(false); }}>Discard</button>
            </div>
          </div>
          <div className="as-dcard">
            <div className="as-seclab" style={{ marginTop: 0 }}>Seating score</div>
            <svg viewBox="0 0 120 120" width="120" height="120" style={{ display: 'block', margin: '0 auto' }}>
              <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(120,110,86,.14)" strokeWidth="10" />
              <circle cx="60" cy="60" r="52" fill="none" stroke="#789A82" strokeWidth="10" strokeLinecap="round" strokeDasharray="327" strokeDashoffset={dash} transform="rotate(-90 60 60)" />
              <text x="60" y="58" textAnchor="middle" fontFamily="Playfair Display, Georgia, serif" fontSize="30" fill="#2A2622">{p.score}</text>
              <text x="60" y="78" textAnchor="middle" fontSize="9" letterSpacing="1.5" fill="#988F80">OF 100</text>
            </svg>
            <div className="as-captext" style={{ textAlign: 'center', marginTop: 6 }}>Share of your concrete goals met — households together, preferred tables, VIP. Affinity refines ties.</div>
          </div>
        </div>
      )}

      <div className="as-vh">
        <div className="as-seclab" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>Version history
          <button className="as-btn" style={{ padding: '5px 11px' }} disabled={busy} onClick={saveVersion}>＋ Save current</button>
        </div>
        {versions.length === 0 ? <div className="as-poolempty" style={{ padding: '10px 2px' }}>No saved versions yet — applying a plan auto-saves one.</div>
          : versions.map((v) => (
            <div className="as-vrow" key={v.id}>
              {/* created_at is stored in UTC — show it in the HOST's local time (host 2026-07-13:
                  a save made at 8:52 PM on the 13th displayed as "07-14 00:52" and read as the wrong day) */}
              <div><b>{v.label}</b><div className="who">{v.saved_by ? `${v.saved_by} · ` : ''}{v.source} · {v.seated || 0} seated · {v.created_at ? new Date(v.created_at).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}</div></div>
              <div style={{ display: 'flex', gap: 6, flex: 'none' }}>
                <button className="as-vlink" disabled={busy} onClick={() => restore(v)}>Restore</button>
                <button className="as-vlink del" disabled={busy} title="Delete this version" onClick={() => deleteVersion(v)}>Delete</button>
              </div>
            </div>
          ))}
      </div>
      <div className="as-note">Nothing changes on its own — Milo suggests, you apply, and every applied plan saves a restore point you can return to. You’re the final decision-maker.</div>
    </div>
  );
}

/* ---- Relationship Groups manager (Phase 5) — social circles beyond households ---- */
const GROUP_COLORS = ['#C6A75A', '#789A82', '#8E7FA8', '#7C92B0', '#B98C3C', '#9B948A', '#B07A6A'];

function GroupsManager({ groups, poolList, onClose, onSave }) {
  useEscClose(onClose);
  const [list, setList] = useState(() => (groups || []).map((g) => ({
    id: g.id, name: g.name || '', color: g.color || GROUP_COLORS[0], guest_ids: [...(g.guest_ids || [])],
  })));
  const [openIdx, setOpenIdx] = useState(null);
  const upd = (i, patch) => setList((L) => L.map((g, j) => (j === i ? { ...g, ...patch } : g)));
  const addGroup = () => setList((L) => [...L, { name: '', color: GROUP_COLORS[L.length % GROUP_COLORS.length], guest_ids: [] }]);
  const del = (i) => setList((L) => L.filter((_, j) => j !== i));
  const toggleMember = (i, gid) => upd(i, { guest_ids: list[i].guest_ids.includes(gid) ? list[i].guest_ids.filter((x) => x !== gid) : [...list[i].guest_ids, gid] });
  const cycleColor = (i) => { const cur = GROUP_COLORS.indexOf(list[i].color); upd(i, { color: GROUP_COLORS[(cur + 1) % GROUP_COLORS.length] }); };
  return (
    <div className="as-ov" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="as-modal" style={{ maxWidth: 600 }}>
        <div className="as-mhead"><div className="mt">Relationship groups</div><button className="as-mx" onClick={onClose}>✕</button></div>
        <div className="as-mbody">
          <div className="as-captext" style={{ marginBottom: 12 }}>Social circles beyond households — church, college, work, neighbors. Milo seats each circle together when it can.</div>
          {list.length === 0 && <div className="as-poolempty" style={{ padding: '8px 2px' }}>No groups yet.</div>}
          {list.map((g, i) => (
            <div className="as-grp" key={i}>
              <div className="as-grphead">
                <button className="as-gcolor" style={{ background: g.color }} onClick={() => cycleColor(i)} title="Change color" />
                <input className="as-inp" style={{ flex: 1 }} value={g.name} onChange={(e) => upd(i, { name: e.target.value })} placeholder="e.g. Church Friends" />
                <button className="as-btn" style={{ padding: '7px 11px' }} onClick={() => setOpenIdx(openIdx === i ? null : i)}>{g.guest_ids.length} · members</button>
                <button className="as-tool del" title="Delete group" onClick={() => del(i)}>🗑</button>
              </div>
              {openIdx === i && (
                <div className="as-grpmembers">
                  {(poolList || []).length === 0 ? <div className="as-poolempty">No confirmed guests yet.</div>
                    : (poolList || []).map((p) => (
                      <label key={p.id} className={`as-mcheck ${g.guest_ids.includes(p.id) ? 'on' : ''}`}>
                        <input type="checkbox" checked={g.guest_ids.includes(p.id)} onChange={() => toggleMember(i, p.id)} />{p.name}
                      </label>
                    ))}
                </div>
              )}
            </div>
          ))}
          <button className="as-btn" style={{ marginTop: 8 }} onClick={addGroup}>＋ Add a group</button>
        </div>
        <div className="as-mfoot"><button className="as-btn" onClick={onClose}>Cancel</button><button className="as-btn gold" onClick={() => onSave(list.filter((g) => (g.name || '').trim()))}>Save groups</button></div>
      </div>
    </div>
  );
}

/* ---- Milo conversational seating (Phase 6) — proposes operations; host applies ---- */
function opLabel(op) {
  switch (op.type) {
    case 'protect_guest': return `Protect ${op.guest || ''}`.trim();
    case 'separate_guests': return `Separate ${op.guest || ''} & ${op.guest_b || ''}`;
    case 'pair_guests': return `Seat ${op.guest || ''} with ${op.guest_b || ''}`;
    case 'create_group': return `Group: ${op.group_name || ''}`;
    case 'set_personality': return `${op.table || 'Table'} → ${op.personality || ''}`;
    case 'explain': return 'Explain the plan';
    default: return op.type;
  }
}

function MiloChat({ onOptimize }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const m = input.trim(); if (!m || busy) return;
    setInput(''); setMsgs((L) => [...L, { role: 'user', text: m }]); setBusy(true);
    try {
      const r = await axios.post(`${API}/api/seating/milo`, { message: m });
      if (r.data && r.data.configured === false) {
        setMsgs((L) => [...L, { role: 'milo', text: "My AI brain isn't connected yet — add an ANTHROPIC_API_KEY to chat. You can still optimize and seat with the controls below." }]);
      } else if (r.data && r.data.ok) {
        const it = r.data.intent || {};
        setMsgs((L) => [...L, { role: 'milo', text: it.reply || 'Done.', ops: it.operations || [], clar: it.clarifying_questions || [] }]);
      } else {
        setMsgs((L) => [...L, { role: 'milo', text: (r.data && r.data.error) || 'Sorry, I couldn’t parse that.' }]);
      }
    } catch (e) { setMsgs((L) => [...L, { role: 'milo', text: 'Something went wrong reaching Milo.' }]); }
    setBusy(false);
  };
  // Mock v3 (host 2026-07-14): plain title in Milo's voice, full-width box with Send at the end,
  // tappable example chips instead of an explanation line. The old "Milo proposes; you apply with
  // the controls…" note was builder-speak, not customer language — removed.
  const EXAMPLES = [
    'Keep the church friends together',
    'Never move Carol',
    'Don’t seat Robert with Linda',
    'Put the kids near the dance floor',
    'Why is Table 3 arranged this way?',
  ];
  return (
    <div className="as-mchat">
      <div className="as-mchathead">Tell Milo how you’d like the room arranged</div>
      {msgs.length > 0 && (
        <div className="as-milolog">
          {msgs.map((m, i) => (
            <div key={i} className={`as-mmsg ${m.role}`}>
              <div className="bub">{m.text}</div>
              {(m.ops || []).length > 0 && (
                <div className="as-mops">
                  {m.ops.map((op, j) => op.type === 'optimize'
                    ? <button key={j} className="as-btn gold" style={{ padding: '5px 11px' }} onClick={onOptimize}>✦ Generate draft</button>
                    : <span key={j} className="as-mop">{opLabel(op)}</span>)}
                </div>
              )}
              {(m.clar || []).map((q, j) => <div key={j} className="as-mclar">? {q}</div>)}
            </div>
          ))}
        </div>
      )}
      <div className="as-mchatin">
        <input className="as-inp" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
          placeholder="Type it the way you’d say it — Milo will propose, you decide."
          aria-label="Tell Milo how you’d like the room arranged" />
        <button className="as-btn gold" disabled={busy} onClick={send}>{busy ? '…' : 'Send'}</button>
      </div>
      <div className="as-mexlab">Try one — tap to use it:</div>
      <div className="as-mexrow">
        {EXAMPLES.map((ex) => (
          <button key={ex} className="as-mex" onClick={() => setInput(ex)}>{ex}</button>
        ))}
      </div>
    </div>
  );
}

/* ---- Export (Phase 8) — framable chart/poster print · caterer meals · QR guest lookup ---- */
const STYLE_CARDS = [['lux', 'Luxury Black & Gold'], ['min', 'Modern Minimal'], ['gar', 'Garden Elegance']];
const PRINT_SIZES = ['8×10', '11×14', '16×20', '18×24', '24×36', '27×40', '30×40']; // standard ready-made frame sizes
const CARD_TEMPLATES = [
  ['5371', 'Escort cards — flat', 'Avery 5371 / 8371 · 10 per sheet · 3.5×2″ · no cutting'],
  ['5302', 'Place cards — tent fold', 'Avery 5302 · 4 per sheet · pre-scored fold to 2×3.5″'],
  ['plain', 'Plain sheet', 'Cut-your-own · 2-up grid'],
];
function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c])); }
// Print/PDF pages open in a new tab — give them an explicit "close & go back" bar (hidden when printing).
const PRINT_BARCSS = '@media screen{body{padding-top:54px}}@media print{.noprint{display:none!important}}.noprint{position:fixed;top:0;left:0;right:0;z-index:99;display:flex;gap:10px;align-items:center;padding:9px 16px;background:#fff;border-bottom:1px solid #ececec;font-family:Helvetica,Arial,sans-serif}.noprint .msg{margin-right:auto;font-size:12px;color:#988F80}.noprint .nb{font-size:13px;border:1px solid #c9c9d0;background:#fff;border-radius:8px;padding:7px 13px;cursor:pointer}.noprint .nb.g{background:linear-gradient(180deg,#d8bd74,#c2a14f);border-color:transparent;font-weight:700;color:#3a2f12}';
const PRINT_BACKBAR = '<div class="noprint"><span class="msg">Opened in a new tab — your seating page is still open behind this.</span><button class="nb g" onclick="window.print()">🖨 Print / Save as PDF</button><button class="nb" onclick="window.close()">✕ Close &amp; go back</button></div>';

function ExportView({ plan, poolList, onOpenDesigner }) {
  const [style, setStyle] = useState('lux');
  const [size, setSize] = useState('24×36');
  const [cardTpl, setCardTpl] = useState('5371');
  // guest order inside each table on every printed output — first name / last name / seat
  // order (seat order = the real chairs, so families print together). Host 2026-07-13.
  const [sortMode, setSortMode] = useState('first');
  // Maître d' blueprint options (host 2026-07-21) — both ON by default.
  const [bpMarkKids, setBpMarkKids] = useState(true);
  const [bpSummary, setBpSummary] = useState(true);
  const [q, setQ] = useState('');
  const [lookup, setLookup] = useState(null);
  const [busy, setBusy] = useState(false);
  const tables = (plan && plan.tables) || [];
  const assignments = (plan && plan.assignments) || [];
  const byId = useMemo(() => { const m = {}; (poolList || []).forEach((g) => { m[g.id] = g; }); return m; }, [poolList]);
  const lookupUrl = `${API}/api/seating/lookup/page`;
  const [shareInfo, setShareInfo] = useState(null);
  const [shareBusy, setShareBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => { (async () => { try { const r = await axios.get(`${API}/api/seating/share/status`); setShareInfo(r.data); } catch (e) { /* */ } })(); }, []);
  const createLink = async () => { setShareBusy(true); try { const r = await axios.post(`${API}/api/seating/share/create`); setShareInfo({ enabled: true, token: r.data.token, url: r.data.url }); } catch (e) { /* */ } setShareBusy(false); };
  const revokeLink = async () => { if (!window.confirm('Revoke the collaboration link? Anyone holding it loses access immediately.')) return; setShareBusy(true); try { await axios.post(`${API}/api/seating/share/revoke`); setShareInfo({ enabled: false }); } catch (e) { /* */ } setShareBusy(false); };
  const copyLink = async () => { try { await navigator.clipboard.writeText(shareInfo.url); setCopied(true); window.setTimeout(() => setCopied(false), 2000); } catch (e) { /* */ } };

  const tryLookup = async () => {
    if (q.trim().length < 2) return;
    setBusy(true);
    try { const r = await axios.get(`${API}/api/seating/lookup`, { params: { q } }); setLookup(r.data); }
    catch (e) { setLookup({ found: false, message: 'Lookup unavailable.' }); }
    setBusy(false);
  };

  const [advice, setAdvice] = useState(null);
  const getAdvice = async (item, extra) => {
    setAdvice({ item, loading: true });
    try {
      const r = await axios.post(`${API}/api/seating/print-advice`, { item, ...(extra || {}) });
      if (r.data && r.data.configured === false) setAdvice({ item, nokey: true });
      else if (r.data && r.data.ok) setAdvice({ item, text: r.data.advice, sources: r.data.sources || [] });
      else setAdvice({ item, error: (r.data && r.data.error) || 'Could not get tips right now.' });
    } catch (e) { setAdvice({ item, error: 'Something went wrong reaching Milo.' }); }
  };

  const seatMapX = useMemo(() => buildSeatMap(tables, assignments), [tables, assignments]);
  // one ordering rule for every print — first/last alphabetical or true seat order (families together)
  const printEntries = (t) => orderedEntries(t, assignments, byId, seatMapX, sortMode);
  const listHtml = (t, markKids = false) => {
    const entries = printEntries(t);
    // markKids (blueprint only): children carry the same ▲ mark as the floor plans.
    const kidMark = (e) => (markKids && isChild(byId[e.gid]) ? '<span class="kd">▲</span>' : '');
    const items = entries.map((e) => (sortMode === 'seat'
      ? `<li><span class="sn">${e.seat}</span>${esc(e.name)}${kidMark(e)}</li>`
      : `<li>${esc(e.name)}${kidMark(e)}</li>`)).join('') || '<li class="e">—</li>';
    return `<${sortMode === 'seat' ? 'ul' : 'ol'} class="gl">${items}</${sortMode === 'seat' ? 'ul' : 'ol'}>`;
  };
  const SN_CSS = '.sn{display:inline-block;min-width:16px;color:#9C7B31;font-family:Helvetica,Arial,sans-serif;font-size:9.5px}ul.gl{list-style:none;padding-left:4px;margin:0}ol.gl{margin:0;padding-left:20px}';

  const printChart = () => {
    const sections = tables.map((t) => `<div class="tbl"><h2>${esc(t.label || 'Table')}</h2>${listHtml(t)}</div>`).join('');
    const theme = { lux: 'body{background:#171310;color:#e9d8a6}h1{color:#fff}h2{color:#E4CE92}li{border-color:#3a2f1a}', min: 'h2{color:#9C7B31}', gar: 'body{background:#f3f6ef}h2{color:#3f6b4e}' }[style] || '';
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Seating Chart</title><style>
      @page{size:${size.replace('×', ' ')} in;margin:.5in}
      body{font-family:Georgia,'Times New Roman',serif;margin:40px;color:#2A2622}
      h1{font-size:31px;font-weight:500;text-align:center;margin:0 0 6px}
      .sub{text-align:center;font-family:Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:.22em;text-transform:uppercase;opacity:.7;margin-bottom:26px}
      .grid{column-count:3;column-gap:30px}
      .tbl{break-inside:avoid;margin-bottom:18px}
      h2{font-size:17px;margin:0 0 6px}
      ol{margin:0;padding-left:20px}
      li{font-size:13px;padding:2px 0;border-bottom:1px solid #e6e6ea}
      li.e{list-style:none;opacity:.5}
      ${SN_CSS}
      ${theme}
      ${PRINT_BARCSS}
    </style></head><body>${PRINT_BACKBAR}<h1>The Seating</h1><div class="sub">${esc((plan && plan.name) || 'Our Celebration')}</div><div class="grid">${sections}</div></body></html>`;
    const win = window.open('', '_blank'); if (!win) return;
    win.document.write(html); win.document.close(); win.focus();
    setTimeout(() => { try { win.print(); } catch (e) { /* */ } }, 350);
  };

  // 3D-D — maître d' / venue-staff blueprint: a top-down room plan (table positions + fixtures)
  // PLUS a per-table roster. Staff-facing, so full names are included. Prints / saves to PDF.
  const printBlueprint = () => {
    const byTable = {}; assignments.forEach((a) => { (byTable[a.table_id] = byTable[a.table_id] || []).push(a.guest_id); });
    const fixtures = (plan && plan.fixtures) || [];
    const kidsAt = (tid) => (byTable[tid] || []).filter((gid) => isChild(byId[gid])).length;
    const tnodes = tables.map((t) => {
      const x = t.x || 0; const y = t.y || 0; const seated = (byTable[t.id] || []).length;
      const kids = kidsAt(t.id);
      const r = 26 + Math.min(20, (t.seat_count || 8));
      const rot = Number(t.rotation) || 0;   // print matches the floor editor — shapes rotate, labels stay readable
      const shape = (t.shape === 'rectangle' || t.shape === 'head')
        ? `<rect x="${x - r}" y="${y - r * 0.6}" width="${r * 2}" height="${r * 1.2}" rx="6" fill="#fff" stroke="#9C7B31" stroke-width="1.5"${rot ? ` transform="rotate(${rot} ${x} ${y})"` : ''}/>`
        : `<circle cx="${x}" cy="${y}" r="${r}" fill="#fff" stroke="#9C7B31" stroke-width="1.5"/>`;
      // host 2026-07-21: each table on the plan states its composition — guests, adults, children
      const compo = (bpMarkKids && seated)
        ? `<text x="${x}" y="${y + 27}" text-anchor="middle" font-size="8.5" fill="${kids ? '#7B5EA7' : '#6F685C'}">${seated - kids}A · ${kids}C</text>`
        : '';
      return `${shape}<text x="${x}" y="${y - 1}" text-anchor="middle" font-size="13" font-weight="bold" fill="#2A2622">${esc(t.label || 'T')}</text><text x="${x}" y="${y + 15}" text-anchor="middle" font-size="10" fill="#6F685C">${seated}/${t.seat_count || 8}</text>${compo}`;
    }).join('');
    const fnodes = fixtures.map((f) => {
      const x = f.x || 0; const y = f.y || 0; const w = Number(f.w) || 40; const h = Number(f.h) || 40;
      const rot = Number(f.rotation) || 0;
      const shape = f.shape === 'circle'
        ? `<circle cx="${x}" cy="${y}" r="${Math.max(w, h) / 2}" fill="#eef0f2" stroke="#C6A75A" stroke-dasharray="5 4"/>`
        : `<rect x="${x - w / 2}" y="${y - h / 2}" width="${w}" height="${h}" rx="6" fill="#eef0f2" stroke="#C6A75A" stroke-dasharray="5 4"${rot ? ` transform="rotate(${rot} ${x} ${y})"` : ''}/>`;
      return `${shape}<text x="${x}" y="${y + 4}" text-anchor="middle" font-size="10" fill="#6F685C">${esc(f.label || f.type)}</text>`;
    }).join('');
    // Auto-fit: the drawing frames YOUR room (rotation-safe bounds), instead of assuming a fixed
    // 920x660 canvas — a rotated/tall layout used to print tiny in a corner and spill to page 2.
    let bx0 = Infinity, by0 = Infinity, bx1 = -Infinity, by1 = -Infinity;
    tables.forEach((t) => { const r = 26 + Math.min(20, (t.seat_count || 8)) + 18;
      bx0 = Math.min(bx0, (t.x || 0) - r); by0 = Math.min(by0, (t.y || 0) - r);
      bx1 = Math.max(bx1, (t.x || 0) + r); by1 = Math.max(by1, (t.y || 0) + r); });
    fixtures.forEach((f) => { const hw = Math.max(Number(f.w) || 40, Number(f.h) || 40) / 2 + 14;
      bx0 = Math.min(bx0, (f.x || 0) - hw); by0 = Math.min(by0, (f.y || 0) - hw);
      bx1 = Math.max(bx1, (f.x || 0) + hw); by1 = Math.max(by1, (f.y || 0) + hw); });
    if (!isFinite(bx0)) { bx0 = 0; by0 = 0; bx1 = 920; by1 = 660; }
    const vb = `${Math.floor(bx0)} ${Math.floor(by0)} ${Math.ceil(bx1 - bx0)} ${Math.ceil(by1 - by0)}`;
    const roster = tables.map((t) => {
      const n = printEntries(t).length;
      const kids = kidsAt(t.id);
      const head = bpMarkKids && n
        ? `${n}/${t.seat_count || 8} · ${n - kids} adults · ${kids} children`
        : `${n}/${t.seat_count || 8}`;
      return `<div class="rt"><h3>${esc(t.label || 'Table')} <span>${head}</span></h3>${listHtml(t, bpMarkKids)}</div>`;
    }).join('');
    // Whole-room summary (host 2026-07-21) — computed live from the same
    // assignments the KPI band uses, never hand-typed.
    const allSeated = assignments.map((a) => a.guest_id);
    const totalKids = allSeated.filter((gid) => isChild(byId[gid])).length;
    const tablesUsed = tables.filter((t) => (byTable[t.id] || []).length > 0).length;
    const seatCap = tables.reduce((s, t) => s + (Number(t.seat_count) || 8), 0);
    const summaryHtml = bpSummary
      ? `<div class="bpsum"><b>${tablesUsed}</b> of ${tables.length} tables in use · <b>${allSeated.length}</b> of ${seatCap} seats used · <b>${allSeated.length - totalKids}</b> adults · <b>${totalKids}</b> children · <b>${allSeated.length}</b> guests total</div>`
      : '';
    const legendHtml = bpMarkKids ? '<div class="bpleg">▲ = child · each table shows adults (A) · children (C)</div>' : '';
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Maître d’ blueprint</title><style>
      @page{size:landscape;margin:.4in}
      body{font-family:Helvetica,Arial,sans-serif;color:#2A2622;margin:24px}
      h1{font-family:Georgia,serif;font-weight:500;font-size:24px;margin:0 0 2px}
      .sub{font-size:10px;letter-spacing:.2em;text-transform:uppercase;color:#9C7B31;margin-bottom:14px}
      .plan{border:1px solid #e6e6ea;border-radius:10px;background:#fafbfc;margin-bottom:20px;page-break-after:always}
      .roster{display:grid;grid-template-columns:repeat(4,1fr);column-gap:22px;row-gap:12px;align-items:start}
      .rt{break-inside:avoid}
      h3{font-family:Georgia,serif;font-size:14px;margin:0 0 4px;border-bottom:1px solid #e6e6ea;padding-bottom:3px}
      h3 span{float:right;font-family:Helvetica,Arial,sans-serif;font-size:10px;color:#9C7B31}
      ol{margin:0;padding-left:18px} li{font-size:11px;padding:1px 0} li.e{list-style:none;opacity:.5}
      .kd{color:#7B5EA7;font-size:9px;margin-left:5px}
      .bpsum{font-size:12px;color:#2A2622;background:#f6f4fa;border:1px solid #e2dcee;border-radius:8px;padding:7px 12px;margin:0 0 12px}
      .bpleg{font-size:10px;color:#7B5EA7;margin:-6px 0 10px}
      ${SN_CSS}
      ${PRINT_BARCSS}
    </style></head><body>${PRINT_BACKBAR}
      <h1>Maître d’ Blueprint</h1><div class="sub">${esc((plan && plan.name) || 'Our Celebration')} · ${tables.length} tables · ${assignments.length} seated</div>
      ${summaryHtml}${legendHtml}
      <svg class="plan" viewBox="${vb}" preserveAspectRatio="xMidYMid meet" style="width:100%;max-height:6.4in;display:block">${fnodes}${tnodes}</svg>
      <div class="roster">${roster}</div>
    </body></html>`;
    const win = window.open('', '_blank'); if (!win) return;
    win.document.write(html); win.document.close(); win.focus();
    setTimeout(() => { try { win.print(); } catch (e) { /* */ } }, 400);
  };

  // Escort / place cards — laid out to fit perforated card stock (no hand-cutting).
  const printCards = () => {
    const cards = [];
    tables.forEach((t) => { printEntries(t).forEach((e) => { cards.push({ name: e.name, table: t.label || 'Table', seat: e.seat }); }); });
    // first/last sort = one alphabetical run for easy entrance pickup; seat sort = stacked per table in chair order
    const keyOf = (n) => { const p = String(n || '').trim().split(/\s+/); return sortMode === 'last' ? `${p[p.length - 1]} ${p[0]}` : String(n || ''); };
    if (sortMode !== 'seat') cards.sort((a, b) => keyOf(a.name).toLowerCase().localeCompare(keyOf(b.name).toLowerCase()));
    if (!cards.length) { return; }
    const perPage = cardTpl === '5371' ? 10 : (cardTpl === '5302' ? 4 : 8);
    const pages = []; for (let i = 0; i < cards.length; i += perPage) pages.push(cards.slice(i, i + perPage));
    const face = (c) => `<div class="nm">${esc(c.name)}</div><div class="tb">${esc(c.table)}</div>`;
    let bodyHtml;
    if (cardTpl === '5302') {
      bodyHtml = pages.map((pg) => `<div class="sheet">${pg.map((c) => `<div class="cell"><div class="half top">${face(c)}</div><div class="fold"></div><div class="half bot">${face(c)}</div></div>`).join('')}</div>`).join('');
    } else {
      bodyHtml = pages.map((pg) => `<div class="sheet">${pg.map((c) => `<div class="card">${face(c)}</div>`).join('')}</div>`).join('');
    }
    const css = {
      '5371': '@page{size:letter;margin:0}.sheet{box-sizing:border-box;width:8.5in;height:11in;padding:.5in .75in;display:grid;grid-template-columns:3.5in 3.5in;grid-auto-rows:2in;page-break-after:always}.card{box-sizing:border-box;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;overflow:hidden;padding:0 .2in}',
      'plain': '@page{size:letter;margin:.5in}.sheet{display:grid;grid-template-columns:1fr 1fr;grid-auto-rows:2.4in;gap:.18in;page-break-after:always}.card{border:1px dashed #c7c7cd;border-radius:6px;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center}',
      '5302': '@page{size:letter;margin:0}.sheet{box-sizing:border-box;width:8.5in;height:11in;padding:1.5in .75in;display:grid;grid-template-columns:3.5in 3.5in;grid-auto-rows:4in;page-break-after:always}.cell{box-sizing:border-box;display:flex;flex-direction:column}.half{height:2in;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center}.half.top{transform:rotate(180deg)}.fold{border-top:1px dashed #ccc}',
    }[cardTpl];
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Escort cards</title><style>body{margin:0;font-family:Georgia,serif;color:#2A2622}${css}.nm{font-family:Georgia,'Times New Roman',serif;font-size:17pt}.tb{font-family:Helvetica,Arial,sans-serif;font-size:10pt;letter-spacing:.12em;text-transform:uppercase;color:#9C7B31;margin-top:5px}${PRINT_BARCSS}</style></head><body>${PRINT_BACKBAR}${bodyHtml}</body></html>`;
    const win = window.open('', '_blank'); if (!win) return;
    win.document.write(html); win.document.close(); win.focus();
    setTimeout(() => { try { win.print(); } catch (e) { /* */ } }, 350);
  };

  const mealRows = tables.map((t) => {
    const ids = assignments.filter((a) => a.table_id === t.id).map((a) => a.guest_id);
    const meals = ids.reduce((s, id) => s + ((((byId[id] || {}).meal_count) || ((byId[id] || {}).needs_meal ? 1 : 0)) || 1), 0);
    const dietary = ids.map((id) => (byId[id] || {}).dietary_requirements).filter(Boolean);
    return { id: t.id, label: t.label, count: ids.length, meals, dietary };
  }).filter((r) => r.count > 0);

  if (tables.length === 0) return <div className="as-ph"><div className="pt">Export</div><div className="pd">Build and seat your tables first — then export a framable chart/poster and the QR guest-lookup here.</div></div>;

  return (
    <div>
      <div className="as-quick">
        <div className="qt">Seating chart — design &amp; print</div>
        <div className="qd">Style the printable chart exactly how you want it: fonts, columns, background color or picture, editable title lines, and per-document table names. Your design saves with the event.</div>
        <div className="as-row"><button className="as-btn gold" onClick={onOpenDesigner}>🖨 Open the Seating Chart Designer</button></div>
      </div>

      <div className="as-seclab">Guest order on printed pieces</div>
      <div className="as-chiprow">
        {[['first', 'First name A–Z'], ['last', 'Last name A–Z'], ['seat', 'Seat order (families together)']].map(([k, l]) => (
          <span key={k} className={`as-pick ${sortMode === k ? 'on' : ''}`} onClick={() => setSortMode(k)}>{l}</span>
        ))}
      </div>
      <div className="as-cardhint">Applies to the poster, the maître d’ blueprint, and the escort/place cards below. Seat order lists each table by its real chairs, so families appear together.</div>

      <div className="as-seclab">Chart style</div>
      <div className="as-styles">
        {STYLE_CARDS.map(([k, label]) => (
          <div key={k} className={`as-style ${style === k ? 'on' : ''}`} onClick={() => setStyle(k)}>
            <div className={`sw ${k}`}>Aa</div><div className="nm">{label}</div>
          </div>
        ))}
      </div>
      <div className="as-seclab">Print size (framable poster)</div>
      <div className="as-chiprow">{PRINT_SIZES.map((s) => <span key={s} className={`as-pick ${size === s ? 'on' : ''}`} onClick={() => setSize(s)}>{s}″</span>)}</div>
      <div className="as-row" style={{ marginTop: 14 }}>
        <button className="as-btn gold" onClick={printChart}>🖨 Print / Save as PDF</button>
        <button className="as-btn" onClick={() => getAdvice('seating poster', { size, style: (STYLE_CARDS.find((s) => s[0] === style) || [])[1] })}>✦ Milo: print &amp; shopping tips</button>
      </div>
      <div className="as-cardhint">Sizes match standard ready-made frames (e.g. Michaels). 24×36 &amp; 18×24 are the easiest to find.</div>

      <div className="as-seclab">Escort / place cards</div>
      <div className="as-captext" style={{ marginBottom: 8 }}>One card per seated guest (name + table), alphabetical for entrance pickup — laid out to fit perforated card stock so you never hand-cut.</div>
      <div className="as-chiprow">{CARD_TEMPLATES.map(([k, label, sub]) => <span key={k} className={`as-pick ${cardTpl === k ? 'on' : ''}`} title={sub} onClick={() => setCardTpl(k)}>{label}</span>)}</div>
      <div className="as-cardhint">{(CARD_TEMPLATES.find((c) => c[0] === cardTpl) || [])[2]}</div>
      <div className="as-row" style={{ marginTop: 12 }}>
        <button className="as-btn gold" onClick={printCards}>🖨 Print cards</button>
        <button className="as-btn" onClick={() => getAdvice('escort / place cards', {})}>✦ Milo: card print tips</button>
      </div>

      <div className="as-seclab">Maître d’ blueprint &amp; sharing</div>
      <div className="as-captext" style={{ marginBottom: 8 }}>A staff-facing run sheet: a top-down room plan (tables + fixtures) plus a per-table guest roster — print it or save to PDF for your maître d’ / venue. To email or text the room: open the 3D view and tap <b>📸 Snapshot</b> for a JPEG. <b>Sending always happens through Communications, on your confirmation — nothing goes out automatically.</b></div>
      <div className="as-chiprow" style={{ marginTop: 2 }}>
        <span className={`as-pick ${bpMarkKids ? 'on' : ''}`} onClick={() => setBpMarkKids((v) => !v)} title="Children get the ▲ mark on the roster and A/C counts on each table">
          {bpMarkKids ? '✓ ' : ''}Identify children
        </span>
        <span className={`as-pick ${bpSummary ? 'on' : ''}`} onClick={() => setBpSummary((v) => !v)} title="Header totals: tables and seats used, adults, children, total guests">
          {bpSummary ? '✓ ' : ''}Include summary
        </span>
      </div>
      <div className="as-row" style={{ marginTop: 8 }}>
        <button className="as-btn gold" onClick={printBlueprint}>🖨 Print maître d’ blueprint / PDF</button>
      </div>

      <div className="as-seclab">Per-table meals — kitchen copy</div>
      {mealRows.length === 0 ? <div className="as-poolempty" style={{ padding: '8px 2px' }}>Seat some guests to see the caterer breakdown.</div> : (
        <div className="as-mealgrid">
          {mealRows.map((r) => (
            <div className="as-meal" key={r.id}>
              <div className="ml">{r.label}</div>
              <div className="mv">{r.meals} meals · {r.count} seated</div>
              {r.dietary.length ? <div className="md">{r.dietary.length} special: {r.dietary.slice(0, 3).join(', ')}{r.dietary.length > 3 ? '…' : ''}</div> : null}
            </div>
          ))}
        </div>
      )}

      <div className="as-seclab">Guest lookup (Oscar)</div>
      <div className="as-lookup">
        <div className="as-qrwrap"><QRCodeSVG value={lookupUrl} size={104} bgColor="transparent" fgColor="#2A2622" /></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="as-captext">Guests scan to find their table &amp; who's with them — read-only, names only. <b>Milo plans the room; Oscar</b> shows each guest the safe slice. Try it:</div>
          <div className="as-row" style={{ marginTop: 8 }}>
            <input className="as-inp" style={{ flex: 1 }} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') tryLookup(); }} placeholder="Type a guest name…" />
            <button className="as-btn" disabled={busy} onClick={tryLookup}>Find seat</button>
          </div>
          {lookup && (
            <div className="as-lookres">
              {!lookup.found ? <span>{lookup.message}</span>
                : lookup.seated ? <span><b>{lookup.name}</b> → <b>{lookup.table}</b>{lookup.tablemates && lookup.tablemates.length ? ` · with ${lookup.tablemates.join(', ')}` : ''}</span>
                  : <span>{lookup.message}</span>}
            </div>
          )}
        </div>
      </div>

      <div className="as-seclab">Collaborate on the layout</div>
      <div className="as-lookup">
        {shareInfo && shareInfo.enabled ? (
          <>
            <div className="as-qrwrap"><QRCodeSVG value={shareInfo.url} size={104} bgColor="transparent" fgColor="#2A2622" /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="as-captext">Share to let family or your planner <b>rearrange the room with you</b>. They move tables &amp; fixtures and save a suggestion — it returns to you as a version you can restore or ignore. They never see your guest list, and <b>you stay the final decision-maker</b>.</div>
              <div className="as-row" style={{ marginTop: 8 }}>
                <input className="as-inp" style={{ flex: 1 }} readOnly value={shareInfo.url} onFocus={(e) => e.target.select()} />
                <button className="as-btn" onClick={copyLink}>{copied ? '✓ Copied' : 'Copy'}</button>
                <button className="as-btn" disabled={shareBusy} onClick={revokeLink}>Revoke</button>
              </div>
            </div>
          </>
        ) : (
          <div style={{ flex: 1 }}>
            <div className="as-captext">Invite someone to help arrange the room — they get an interactive 3D view to move tables &amp; fixtures and save suggestions. Privacy-safe: collaborators see the layout and seat counts, never your guest list. The link is revocable anytime.</div>
            <button className="as-btn" style={{ marginTop: 8 }} disabled={shareBusy} onClick={createLink}>⛓ Create collaboration link</button>
          </div>
        )}
      </div>

      {advice && (
        <div className="as-advice">
          <div className="as-advicehd"><span className="as-orb2">M</span> Print &amp; shopping tips · {advice.item}</div>
          {advice.loading ? <div className="as-captext"><MiloThinking label="Milo is checking current options & deals…" /></div>
            : advice.nokey ? <div className="as-captext">Milo's AI brain isn't connected — add an ANTHROPIC_API_KEY for live print &amp; deal tips. Your chart, cards, and poster all print without it.</div>
              : advice.error ? <div className="as-derr">{advice.error}</div>
                : (
                  <>
                    <div className="as-advicebody">{(advice.text || '').split('\n').filter((l) => l.trim()).map((ln, i) => <div key={i} className="ln">{ln.replace(/^[-•*]\s*/, '• ')}</div>)}</div>
                    {(advice.sources || []).length > 0 && (
                      <div className="as-srcs">{advice.sources.map((s, i) => <a key={i} className="as-src" href={s.url} target="_blank" rel="noreferrer">↗ {s.title}</a>)}</div>
                    )}
                    <div className="as-note" style={{ marginTop: 8 }}>Milo suggests &amp; links — it never buys or enters payment. You place any order.</div>
                  </>
                )}
        </div>
      )}
      <div className="as-note">Printing happens on your device — nothing is sent. Guest photos on cards are a Phase-2 follow-up; the public lookup page wires to the QR at publish.</div>
    </div>
  );
}
